// Tank peint pièce par pièce (cosmétiques refaits) : couleur libre, matière, motif et autocollant par pièce.
// Chaque pièce est une base pleine, puis une couche « matière + motif + autocollant » découpée à la silhouette de la
// pièce par un masque. En partie, l'ensemble est cuit une fois en image (voir bakeTank) : le coût par image reste celui
// d'un sprite, quelle que soit la richesse du dessin. La hitbox reste le cercle serveur ; la couleur du camp reste au
// cœur de la tourelle, et les contours restent sombres pour que la silhouette se lise toujours.
import { Container, FillGradient, Graphics } from 'pixi.js';
import type { ClassNode } from '../../../../shared/catalog/classes';
import type { PartId, PartPaint, TankLook } from '../../../../shared/cosmetics';
import { ART_R, drawBarrel, drawHullFamily, type Palette } from './art';
import { drawFamilyDetail, drawRankPips } from './variants';
import { glossCore, shadeBarrels, shadeDome, shadeHull } from './shade';

const R = ART_R;

// ─── Couleurs ─────────────────────────────────────────────────────────

const ch = (c: number) => [(c >> 16) & 255, (c >> 8) & 255, c & 255];
const rgb = (r: number, g: number, b: number) => (Math.round(Math.max(0, Math.min(255, r))) << 16) | (Math.round(Math.max(0, Math.min(255, g))) << 8) | Math.round(Math.max(0, Math.min(255, b)));
export function mix(a: number, b: number, t: number): number {
  const [r1, g1, b1] = ch(a);
  const [r2, g2, b2] = ch(b);
  return rgb(r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t);
}
const lighten = (c: number, t: number) => mix(c, 0xffffff, t);
const darken = (c: number, t: number) => mix(c, 0x000000, t);
function hueShift(c: number, deg: number): number {
  const [r, g, b] = ch(c).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d) h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h = (h * 60 + deg + 360) % 360;
  const C = (1 - Math.abs(2 * l - 1)) * s;
  const X = C * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - C / 2;
  const [r1, g1, b1] = h < 60 ? [C, X, 0] : h < 120 ? [X, C, 0] : h < 180 ? [0, C, X] : h < 240 ? [0, X, C] : h < 300 ? [X, 0, C] : [C, 0, X];
  return rgb((r1 + m) * 255, (g1 + m) * 255, (b1 + m) * 255);
}
/** Teinte (0..360), saturation et luminosité (0..1) d'une couleur. */
function toHsl(c: number): [number, number, number] {
  const [r, g, b] = ch(c).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  const s = d === 0 ? 0 : Math.min(1, d / (1 - Math.abs(2 * l - 1)));
  if (d) h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [(h * 60 + 360) % 360, s, l];
}
function fromHsl(h: number, s: number, l: number): number {
  h = ((h % 360) + 360) % 360;
  const C = (1 - Math.abs(2 * l - 1)) * s;
  const X = C * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - C / 2;
  const [r1, g1, b1] = h < 60 ? [C, X, 0] : h < 120 ? [X, C, 0] : h < 180 ? [0, C, X] : h < 240 ? [0, X, C] : h < 300 ? [X, 0, C] : [C, 0, X];
  return rgb((r1 + m) * 255, (g1 + m) * 255, (b1 + m) * 255);
}
/** Version saturée et lumineuse d'une couleur (lueurs, nébuleuses) ; un gris prend une teinte cyan. */
function vivid(c: number, sMin = 0.7, lMin = 0.55, lMax = 0.75): number {
  const [h, s, l] = toHsl(c);
  return fromHsl(s < 0.08 ? 190 : h, Math.max(s, sMin), Math.min(lMax, Math.max(l, lMin)));
}
/** Couleur de laque : saturée et profonde (un gris donne une laque noire). */
function lacquerOf(c: number): number {
  const [h, s, l] = toHsl(c);
  return s < 0.08 ? darken(c, 0.45) : fromHsl(h, Math.min(1, s * 1.2 + 0.15), Math.min(0.5, Math.max(0.3, l)));
}
/** Émail de la porcelaine : bleu de cobalt, ou la couleur choisie si elle est franche. */
function enamelOf(c: number): number {
  const [h, s] = toHsl(c);
  return s < 0.18 ? 0x2453b3 : fromHsl(h, Math.max(s, 0.6), 0.38);
}
/** Tache irrégulière (polygone) autour de (x, y). */
function blob(rnd: () => number, x: number, y: number, r: number, n = 9, jag = 0.45, sx = 1): number[] {
  const pts: number[] = [];
  const a0 = rnd() * Math.PI * 2;
  for (let k = 0; k < n; k++) {
    const t = a0 + (k / n) * Math.PI * 2;
    const rr = r * (1 - jag / 2 + rnd() * jag);
    pts.push(x + Math.cos(t) * rr * sx, y + Math.sin(t) * rr);
  }
  return pts;
}
/** Étoile à quatre branches (étoiles, reflets). */
function sparkle(g: Graphics, x: number, y: number, r: number, color: number, alpha = 1): void {
  const k = r * 0.18;
  g.poly([x - r, y, x - k, y - k, x, y - r, x + k, y - k, x + r, y, x + k, y + k, x, y + r, x - k, y + k]).fill({ color, alpha });
}
/** Pain d'épices : brun doré de biscuit cuit, tiré vers la couleur choisie (un gris donne un biscuit nature). */
function gingerOf(c: number): number {
  const [h, s, l] = toHsl(c);
  const base = 0xa8662f;
  return s < 0.08 ? mix(base, c, 0.22) : mix(base, fromHsl(h, Math.min(0.75, s), Math.min(0.5, Math.max(0.32, l))), 0.32);
}
/** Velours : couleur profonde et saturée (un gris donne un velours anthracite). */
function velvetOf(c: number): number {
  const [h, s, l] = toHsl(c);
  return s < 0.08 ? darken(c, 0.4) : fromHsl(h, Math.min(1, s * 1.1 + 0.12), Math.min(0.4, Math.max(0.2, l * 0.75)));
}
/** Verre de Noël : couleur saturée et profonde de boule de sapin (un gris donne un verre argenté). */
function glassOf(c: number): number {
  const [h, s, l] = toHsl(c);
  return s < 0.08 ? mix(c, 0x9aa6b8, 0.45) : fromHsl(h, Math.max(0.75, Math.min(1, s * 1.2)), Math.min(0.46, Math.max(0.3, l)));
}
/** Couleur CSS avec transparence (arrêts de dégradé). */
const rgba = (c: number, a: number) => `rgba(${(c >> 16) & 255},${(c >> 8) & 255},${c & 255},${a})`;
/** Reflet en lentille (fin aux deux bouts) de x0 à x1 autour de yc, épaisseur th, cambré de `curve`. */
function lensPts(x0: number, x1: number, yc: number, th: number, curve: number): number[] {
  const top: number[] = [];
  const bot: number[] = [];
  for (let k = 0; k <= 12; k++) {
    const t = k / 12;
    const x = x0 + (x1 - x0) * t;
    const y = yc - Math.sin(t * Math.PI) * curve;
    const w = Math.sin(t * Math.PI) * th;
    top.push(x, y - w);
    bot.unshift(x, y + w * 0.4);
  }
  for (let k = 0; k < bot.length; k += 2) top.push(bot[k], bot[k + 1]);
  return top;
}
/** Étoile à n branches (rayons extérieur et intérieur) autour de (x, y). */
function starPts(x: number, y: number, n: number, ro: number, ri: number, a0 = -Math.PI / 2): number[] {
  const pts: number[] = [];
  for (let i = 0; i < n * 2; i++) {
    const a = a0 + (i * Math.PI) / n;
    const r = i % 2 ? ri : ro;
    pts.push(x + Math.cos(a) * r, y + Math.sin(a) * r);
  }
  return pts;
}

/** Matières dont la base diffère nettement de la couleur : sur les tubes (dessinés à la couleur pleine), on repose la base. */
const UNDERLAID = new Set(['mat:glace', 'mat:obsidienne', 'mat:holo', 'mat:bois', 'mat:abysse', 'mat:galaxie', 'mat:marbre', 'mat:rouille', 'mat:spectral', 'mat:laque', 'mat:gres', 'mat:porcelaine', 'mat:paindepices', 'mat:velours', 'mat:boulenoel']);

/** Contour d'une pièce : toujours sombre, teinté par sa couleur (silhouette lisible sur tout fond). */
const outlineOf = (c: number) => mix(darken(c, 0.78), 0x101317, 0.5);

/** Hachage déterministe (motifs et craquelures identiques chez tous les joueurs et à chaque cuisson). */
function seeded(seed: number): () => number {
  let s = seed >>> 0 || 1;
  return () => {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    return (s >>> 0) / 4294967296;
  };
}

// ─── Silhouettes des pièces (repère du tank, rayon de référence ART_R) ─────

/** Contour arrondi d'une partie de pièce (rectangle à coins arrondis ; un cercle a w = h = 2r). */
interface Rim {
  x: number;
  y: number;
  w: number;
  h: number;
  r: number;
}

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Contours réels de la pièce (glaçage, fourrure, reflets par pièce) ; absent sur la boîte des tubes. */
  rims?: Rim[];
}

function treadsShape(g: Graphics): Box {
  for (const side of [-1, 1]) g.roundRect(-0.68 * R, side * 0.66 * R - 0.19 * R, 1.36 * R, 0.38 * R, 0.12 * R);
  return { x: -0.68 * R, y: -0.85 * R, w: 1.36 * R, h: 1.7 * R, rims: [-1, 1].map((side) => ({ x: -0.68 * R, y: side * 0.66 * R - 0.19 * R, w: 1.36 * R, h: 0.38 * R, r: 0.12 * R })) };
}
function hullShape(g: Graphics): Box {
  g.roundRect(-0.64 * R, -0.52 * R, 1.28 * R, 1.04 * R, 0.18 * R);
  return { x: -0.64 * R, y: -0.52 * R, w: 1.28 * R, h: 1.04 * R, rims: [{ x: -0.64 * R, y: -0.52 * R, w: 1.28 * R, h: 1.04 * R, r: 0.18 * R }] };
}
function domeShape(g: Graphics): Box {
  g.circle(0, 0, 0.5 * R);
  return { x: -0.5 * R, y: -0.5 * R, w: R, h: R, rims: [{ x: -0.5 * R, y: -0.5 * R, w: R, h: R, r: 0.5 * R }] };
}

const isRound = (m: Rim) => Math.abs(m.w - m.h) < 0.01 && m.r * 2 >= m.w - 0.01;
/** Trace le contour `m` rentré de `inset` (à remplir ou à tracer ensuite). */
function rimPath(g: Graphics, m: Rim, inset = 0): Graphics {
  if (isRound(m)) return g.circle(m.x + m.w / 2, m.y + m.h / 2, Math.max(0.1, m.w / 2 - inset));
  return g.roundRect(m.x + inset, m.y + inset, m.w - 2 * inset, m.h - 2 * inset, Math.max(0, m.r - inset));
}
interface TracePt {
  x: number;
  y: number;
  /** Normale sortante. */
  nx: number;
  ny: number;
  /** Abscisse le long du contour. */
  s: number;
}
/** Points réguliers le long du contour `m` rentré de `inset`, avec normales (glaçage, fourrure). */
function rimTrace(m: Rim, inset: number, step = 0.5): { pts: TracePt[]; len: number } {
  const x0 = m.x + inset;
  const y0 = m.y + inset;
  const w = m.w - 2 * inset;
  const h = m.h - 2 * inset;
  const r = Math.max(0.01, Math.min(m.r - inset, w / 2, h / 2));
  const pts: TracePt[] = [];
  let s = 0;
  const push = (x: number, y: number, nx: number, ny: number) => {
    const last = pts[pts.length - 1];
    if (last) s += Math.hypot(x - last.x, y - last.y);
    pts.push({ x, y, nx, ny, s });
  };
  const side = (ax: number, ay: number, bx: number, by: number, nx: number, ny: number) => {
    const n = Math.max(1, Math.ceil(Math.hypot(bx - ax, by - ay) / step));
    for (let i = 0; i < n; i++) push(ax + ((bx - ax) * i) / n, ay + ((by - ay) * i) / n, nx, ny);
  };
  const corner = (cx: number, cy: number, a0: number) => {
    const n = Math.max(2, Math.ceil((r * Math.PI) / 2 / step));
    for (let i = 0; i < n; i++) {
      const a = a0 + ((Math.PI / 2) * i) / n;
      push(cx + Math.cos(a) * r, cy + Math.sin(a) * r, Math.cos(a), Math.sin(a));
    }
  };
  side(x0 + r, y0, x0 + w - r, y0, 0, -1);
  corner(x0 + w - r, y0 + r, -Math.PI / 2);
  side(x0 + w, y0 + r, x0 + w, y0 + h - r, 1, 0);
  corner(x0 + w - r, y0 + h - r, 0);
  side(x0 + w - r, y0 + h, x0 + r, y0 + h, 0, 1);
  corner(x0 + r, y0 + h - r, Math.PI / 2);
  side(x0, y0 + h - r, x0, y0 + r, -1, 0);
  corner(x0 + r, y0 + r, Math.PI);
  const first = pts[0];
  const last = pts[pts.length - 1];
  return { pts, len: s + Math.hypot(first.x - last.x, first.y - last.y) };
}

// ─── Matières ─────────────────────────────────────────────────────────

/** Remplissage de base d'une pièce selon sa matière (dégradé en coordonnées locales de la forme). */
function baseFill(p: PartPaint): number | FillGradient {
  const c = p.color;
  const lin = (stops: [number, number][], sx = 0, sy = 0, ex = 1, ey = 1) =>
    new FillGradient({ type: 'linear', start: { x: sx, y: sy }, end: { x: ex, y: ey }, colorStops: stops.map(([offset, color]) => ({ offset, color })), textureSpace: 'local' });
  switch (p.mat) {
    case 'mat:satin':
      return lin([[0, lighten(c, 0.22)], [0.55, c], [1, darken(c, 0.28)]]);
    case 'mat:brosse':
      return lin([[0, darken(c, 0.12)], [0.35, lighten(c, 0.2)], [0.65, c], [1, darken(c, 0.22)]], 0, 0, 0, 1);
    case 'mat:carbone':
      return darken(c, 0.62);
    case 'mat:chrome': {
      const k = mix(c, 0xb8c2cc, 0.5);
      return lin([[0, darken(k, 0.6)], [0.22, lighten(k, 0.5)], [0.42, darken(k, 0.35)], [0.58, lighten(k, 0.65)], [0.8, darken(k, 0.5)], [1, lighten(k, 0.15)]], 0, 0, 0.35, 1);
    }
    case 'mat:nacre':
      return lin([[0, lighten(hueShift(c, -35), 0.55)], [0.35, lighten(c, 0.45)], [0.7, lighten(hueShift(c, 45), 0.5)], [1, lighten(hueShift(c, 90), 0.35)]]);
    case 'mat:or': {
      const k = (v: number) => mix(v, c, 0.18);
      return lin([[0, k(0x8a6514)], [0.25, k(0xffe08a)], [0.5, k(0xb8860b)], [0.72, k(0xfff1bf)], [1, k(0x7a5a12)]], 0, 0, 0.5, 1);
    }
    case 'mat:neon':
      return darken(c, 0.82);
    case 'mat:magma':
      return mix(0x1d1512, c, 0.12);
    case 'mat:glace': {
      const k = mix(0xa9d3ee, c, 0.3);
      return lin([[0, lighten(k, 0.5)], [0.4, lighten(k, 0.1)], [0.75, k], [1, darken(mix(k, 0x3d7fb5, 0.35), 0.15)]], 0, 0, 0.6, 1);
    }
    case 'mat:obsidienne':
      return lin([[0, mix(0x2c2b36, c, 0.2)], [0.45, mix(0x0a0a0e, c, 0.07)], [1, mix(0x17161d, c, 0.14)]], 0, 0, 0.4, 1);
    case 'mat:holo': {
      const v = lighten(vivid(c, 0.75, 0.55, 0.65), 0.25);
      return lin([[0, hueShift(v, -70)], [0.25, v], [0.5, hueShift(v, 70)], [0.75, hueShift(v, 140)], [1, hueShift(v, 210)]], 0, 0, 1, 0.7);
    }
    case 'mat:bois': {
      const w = mix(0x9b6b3d, c, 0.3);
      return lin([[0, lighten(w, 0.12)], [0.5, w], [1, darken(w, 0.18)]], 0, 0, 0, 1);
    }
    case 'mat:abysse':
      return lin([[0, mix(0x0b2a4a, c, 0.16)], [0.55, mix(0x051530, c, 0.1)], [1, mix(0x02060f, c, 0.05)]], 0, 0, 0.3, 1);
    case 'mat:galaxie':
      return lin([[0, mix(0x120a2e, c, 0.2)], [0.5, mix(0x05040e, c, 0.05)], [1, mix(0x0c0a24, hueShift(c, 50), 0.18)]]);
    case 'mat:marbre': {
      const k = mix(0xf0ece6, c, 0.14);
      return lin([[0, lighten(k, 0.55)], [0.55, k], [1, darken(k, 0.1)]]);
    }
    case 'mat:rouille':
      return lin([[0, mix(0x8c4a22, c, 0.12)], [0.5, mix(0x6a3418, c, 0.1)], [1, mix(0x45220f, c, 0.12)]]);
    case 'mat:spectral':
      return lin([[0, mix(0x1a2030, c, 0.3)], [1, mix(0x0a0d16, c, 0.16)]], 0, 0, 0.3, 1);
    case 'mat:laque': {
      const v = lacquerOf(c);
      return lin([[0, lighten(v, 0.18)], [0.35, v], [1, darken(v, 0.5)]], 0, 0, 0.4, 1);
    }
    case 'mat:gres': {
      const k = mix(0xd4a76a, c, 0.25);
      return lin([[0, lighten(k, 0.12)], [1, darken(k, 0.12)]], 0, 0, 0, 1);
    }
    case 'mat:porcelaine': {
      const k = mix(0xf6f7f9, c, 0.13);
      return lin([[0, 0xffffff], [0.5, k], [1, darken(k, 0.14)]]);
    }
    case 'mat:paindepices': {
      const k = gingerOf(c);
      return lin([[0, lighten(k, 0.16)], [0.5, k], [1, darken(k, 0.24)]], 0, 0, 0.3, 1);
    }
    case 'mat:velours': {
      const v = velvetOf(c);
      return lin([[0, lighten(v, 0.12)], [0.45, v], [1, darken(v, 0.45)]], 0, 0, 0.25, 1);
    }
    case 'mat:boulenoel': {
      const v = glassOf(c);
      return lin([[0, lighten(v, 0.22)], [0.4, v], [1, darken(v, 0.5)]], 0, 0, 0.3, 1);
    }
    default:
      return c;
  }
}

/** Détails de matière par-dessus la base (découpés par le masque de la pièce). */
function materialOverlay(g: Graphics, p: PartPaint, b: Box, seed: number): void {
  const c = p.color;
  const rnd = seeded(seed);
  switch (p.mat) {
    case 'mat:brosse':
      for (let y = b.y; y < b.y + b.h; y += 1.1) g.moveTo(b.x, y).lineTo(b.x + b.w, y).stroke({ width: 0.45, color: rnd() < 0.5 ? 0xffffff : 0x000000, alpha: 0.08 + rnd() * 0.1 });
      break;
    case 'mat:carbone': {
      const s = 2.4;
      for (let x = b.x; x < b.x + b.w; x += s) {
        for (let y = b.y; y < b.y + b.h; y += s) {
          const odd = (Math.round((x - b.x) / s) + Math.round((y - b.y) / s)) % 2 === 0;
          g.rect(x, y, s, s).fill({ color: odd ? lighten(darken(c, 0.55), 0.18) : darken(c, 0.75), alpha: 0.9 });
          g.moveTo(x, y + (odd ? 0 : s)).lineTo(x + s, y + (odd ? s : 0)).stroke({ width: 0.4, color: 0xffffff, alpha: 0.12 });
        }
      }
      break;
    }
    case 'mat:chrome':
    case 'mat:or':
      // Reflet spéculaire en diagonale.
      g.poly([b.x + b.w * 0.15, b.y, b.x + b.w * 0.32, b.y, b.x + b.w * 0.12, b.y + b.h, b.x - b.w * 0.05, b.y + b.h]).fill({ color: 0xffffff, alpha: 0.22 });
      g.poly([b.x + b.w * 0.4, b.y, b.x + b.w * 0.46, b.y, b.x + b.w * 0.26, b.y + b.h, b.x + b.w * 0.2, b.y + b.h]).fill({ color: 0xffffff, alpha: 0.14 });
      break;
    case 'mat:nacre':
      for (let i = 0; i < 5; i++) {
        const y = b.y + (b.h * (i + 0.5)) / 5;
        g.moveTo(b.x, y).bezierCurveTo(b.x + b.w * 0.3, y - 3, b.x + b.w * 0.6, y + 3, b.x + b.w, y).stroke({ width: 1.6, color: lighten(hueShift(c, i * 50), 0.7), alpha: 0.25 });
      }
      break;
    case 'mat:magma': {
      // Craquelures incandescentes : lignes brisées lumineuses sur roche sombre.
      const hot = lighten(mix(c, 0xff6a1a, 0.6), 0.25);
      for (let k = 0; k < 7; k++) {
        let x = b.x + rnd() * b.w;
        let y = b.y + rnd() * b.h;
        g.moveTo(x, y);
        for (let s = 0; s < 4; s++) {
          x += (rnd() - 0.5) * b.w * 0.35;
          y += (rnd() - 0.5) * b.h * 0.35;
          g.lineTo(x, y);
        }
        g.stroke({ width: 2.4, color: hot, alpha: 0.35 }).stroke({ width: 0.9, color: lighten(hot, 0.6), alpha: 0.95 });
      }
      break;
    }
    default:
      themedMaterial(g, p, b, rnd);
      break;
  }
}

/** Matières des collections à thème (Banquise, Volcan, Cyber…). */
function themedMaterial(g: Graphics, p: PartPaint, b: Box, rnd: () => number): void {
  const c = p.color;
  const area = b.w * b.h;
  const X = () => b.x + rnd() * b.w;
  const Y = () => b.y + rnd() * b.h;
  switch (p.mat) {
    case 'mat:glace': {
      // Facettes givrées, fissures blanches anguleuses, reflets francs.
      for (let i = 0; i < Math.ceil(area / 150); i++) g.poly(blob(rnd, X(), Y(), 3 + rnd() * 5, 5, 0.6)).fill({ color: rnd() < 0.5 ? 0xffffff : mix(0x8cc8ea, c, 0.3), alpha: 0.12 + rnd() * 0.12 });
      for (let k = 0; k < Math.max(3, Math.ceil(area / 130)); k++) {
        let x = X();
        let y = Y();
        let a = rnd() * Math.PI * 2;
        g.moveTo(x, y);
        const pts: [number, number][] = [];
        for (let s = 0; s < 3 + Math.floor(rnd() * 2); s++) {
          a += (s % 2 ? 1 : -1) * (0.5 + rnd() * 0.7);
          const l = 2.5 + rnd() * 3.5;
          x += Math.cos(a) * l;
          y += Math.sin(a) * l;
          g.lineTo(x, y);
          pts.push([x, y]);
        }
        // Une ramification courte depuis un coude.
        const [bx, by] = pts[Math.floor(rnd() * pts.length)];
        const ba = a + (rnd() < 0.5 ? 1 : -1) * (1 + rnd() * 0.6);
        g.moveTo(bx, by).lineTo(bx + Math.cos(ba) * 2.2, by + Math.sin(ba) * 2.2).lineTo(bx + Math.cos(ba + 0.6) * 3.6, by + Math.sin(ba + 0.6) * 3.6);
      }
      g.stroke({ width: 1.3, color: mix(0x1f5a8a, c, 0.3), alpha: 0.2, join: 'miter' }).stroke({ width: 0.7, color: 0xffffff, alpha: 0.95, join: 'miter' });
      g.poly([b.x + b.w * 0.12, b.y, b.x + b.w * 0.3, b.y, b.x + b.w * 0.08, b.y + b.h, b.x - b.w * 0.1, b.y + b.h]).fill({ color: 0xffffff, alpha: 0.26 });
      g.poly([b.x + b.w * 0.38, b.y, b.x + b.w * 0.43, b.y, b.x + b.w * 0.21, b.y + b.h, b.x + b.w * 0.16, b.y + b.h]).fill({ color: 0xffffff, alpha: 0.2 });
      for (let i = 0; i < Math.max(2, Math.ceil(area / 400)); i++) sparkle(g, X(), Y(), 1.2 + rnd() * 1.2, 0xffffff, 0.9);
      break;
    }
    case 'mat:obsidienne': {
      // Verre volcanique : cassures conchoïdales (arcs concentriques clairs) et reflet net.
      const sheen = lighten(mix(c, 0xb8c0d8, 0.45), 0.3);
      for (let i = 0; i < Math.max(2, Math.ceil(area / 300)); i++) {
        const cx = X();
        const cy = Y();
        const a0 = rnd() * Math.PI * 2;
        const span = 1.1 + rnd() * 1;
        const r0 = 3 + rnd() * 6;
        for (let j = 0; j < 3; j++) {
          const r = r0 + j * 1.8;
          for (let k = 0; k <= 10; k++) {
            const t = a0 + (span * k) / 10;
            if (k === 0) g.moveTo(cx + Math.cos(t) * r, cy + Math.sin(t) * r);
            else g.lineTo(cx + Math.cos(t) * r, cy + Math.sin(t) * r);
          }
          g.stroke({ width: j === 0 ? 0.8 : 0.5, color: sheen, alpha: 0.5 - j * 0.14 });
        }
      }
      g.poly([b.x + b.w * 0.1, b.y, b.x + b.w * 0.34, b.y, b.x + b.w * 0.12, b.y + b.h, b.x - b.w * 0.12, b.y + b.h]).fill({ color: 0xffffff, alpha: 0.1 });
      g.poly([b.x + b.w * 0.2, b.y, b.x + b.w * 0.25, b.y, b.x + b.w * 0.03, b.y + b.h, b.x - b.w * 0.02, b.y + b.h]).fill({ color: 0xffffff, alpha: 0.3 });
      g.poly([b.x + b.w * 0.62, b.y, b.x + b.w * 0.645, b.y, b.x + b.w * 0.425, b.y + b.h, b.x + b.w * 0.4, b.y + b.h]).fill({ color: lighten(sheen, 0.4), alpha: 0.22 });
      break;
    }
    case 'mat:holo': {
      // Irisé : bandes de teintes voisines en diagonale, lignes de balayage horizontales.
      const v = lighten(vivid(c, 0.75, 0.55, 0.65), 0.3);
      for (let i = 0; i < 7; i++) {
        const x = b.x - b.h * 0.5 + ((b.w + b.h) * i) / 7 + rnd() * 3;
        const w = 2 + rnd() * 4;
        g.poly([x, b.y + b.h, x + w, b.y + b.h, x + w + b.h * 0.6, b.y, x + b.h * 0.6, b.y]).fill({ color: lighten(hueShift(v, i * 55), 0.3), alpha: 0.22 });
      }
      for (let y = b.y + 0.5, i = 0; y < b.y + b.h; y += 1.4, i++) g.moveTo(b.x, y).lineTo(b.x + b.w, y);
      g.stroke({ width: 0.45, color: 0x10131a, alpha: 0.16 });
      for (let y = b.y + 2, i = 0; y < b.y + b.h; y += 5.6, i++) g.moveTo(b.x, y).lineTo(b.x + b.w, y);
      g.stroke({ width: 0.5, color: 0xffffff, alpha: 0.35 });
      // Quelques bandes « glitch » décalées.
      for (let i = 0; i < Math.max(2, Math.ceil(area / 500)); i++) g.rect(X() - 4, Y(), 4 + rnd() * 8, 0.8 + rnd() * 0.7).fill({ color: hueShift(v, 150 + rnd() * 60), alpha: 0.45 });
      g.poly([b.x + b.w * 0.15, b.y, b.x + b.w * 0.28, b.y, b.x + b.w * 0.08, b.y + b.h, b.x - b.w * 0.05, b.y + b.h]).fill({ color: 0xffffff, alpha: 0.22 });
      break;
    }
    case 'mat:bois': {
      // Veinures ondulées parallèles qui contournent les nœuds.
      const w = mix(0x9b6b3d, c, 0.3);
      const dark = darken(w, 0.55);
      const knots = Array.from({ length: Math.max(1, Math.round(area / 450)) }, () => ({ x: X(), y: Y(), r: 1.4 + rnd() * 1.4 }));
      const ph = rnd() * 6;
      for (let y = b.y - 2; y < b.y + b.h + 2; y += 1.5 + rnd() * 1) {
        const at = (x: number) => {
          let yy = y + Math.sin(x * 0.17 + ph + y * 0.06) * 0.6;
          for (const k of knots) {
            const dx = x - k.x;
            const dy = yy - k.y;
            yy += (dy >= 0 ? 1 : -1) * k.r * 1.6 * Math.exp(-(dx * dx) / (k.r * k.r * 6) - (dy * dy) / (k.r * k.r * 9));
          }
          return yy;
        };
        g.moveTo(b.x - 1, at(b.x - 1));
        for (let x = b.x + 1; x <= b.x + b.w + 2; x += 2) g.lineTo(x, at(x));
        g.stroke({ width: 0.35 + rnd() * 0.55, color: rnd() < 0.2 ? lighten(w, 0.25) : dark, alpha: 0.3 + rnd() * 0.35 });
      }
      for (const k of knots) {
        g.ellipse(k.x, k.y, k.r * 1.5, k.r).fill({ color: darken(w, 0.3), alpha: 0.85 }).stroke({ width: 0.5, color: dark, alpha: 0.8 });
        g.ellipse(k.x, k.y, k.r * 0.8, k.r * 0.5).fill({ color: darken(w, 0.6), alpha: 0.9 });
      }
      break;
    }
    case 'mat:abysse': {
      // Bleu nuit, voiles de lumière et photophores qui brillent dans la couleur choisie.
      const glow = vivid(c, 0.7, 0.55, 0.68);
      for (let i = 0; i < Math.max(2, Math.ceil(area / 300)); i++) {
        const y = Y();
        g.moveTo(b.x - 2, y).bezierCurveTo(b.x + b.w * 0.3, y - 4, b.x + b.w * 0.6, y + 4, b.x + b.w + 2, y - 1).stroke({ width: 2.5 + rnd() * 2, color: glow, alpha: 0.06 });
      }
      for (let i = 0; i < Math.max(3, Math.ceil(area / 110)); i++) {
        const x = X();
        const y = Y();
        const r = 0.7 + rnd() * 1.3;
        g.circle(x, y, r * 3.4).fill({ color: glow, alpha: 0.1 });
        g.circle(x, y, r * 1.9).fill({ color: glow, alpha: 0.22 });
        g.circle(x, y, r).fill({ color: lighten(glow, 0.4), alpha: 0.95 });
        g.circle(x, y, r * 0.45).fill({ color: 0xffffff, alpha: 0.9 });
      }
      // Rangées de petits points (photophores).
      for (let i = 0; i < Math.max(2, Math.ceil(area / 160)); i++) {
        let x = X();
        let y = Y();
        const a = rnd() * Math.PI * 2;
        for (let k = 0; k < 4; k++, x += Math.cos(a) * 1.8, y += Math.sin(a) * 1.8) g.circle(x, y, 0.45).fill({ color: lighten(glow, 0.5), alpha: 0.85 });
      }
      for (let i = 0; i < Math.min(160, Math.ceil(area / 30)); i++) g.circle(X(), Y(), 0.25 + rnd() * 0.3).fill({ color: lighten(glow, 0.3), alpha: 0.3 + rnd() * 0.5 });
      break;
    }
    case 'mat:galaxie': {
      // Espace profond : nébuleuse (couleur et teintes voisines) le long d'une bande, poussière sombre, étoiles.
      const neb = vivid(c, 0.75, 0.55, 0.62);
      const tints = [neb, hueShift(neb, 45), hueShift(neb, -50), lighten(neb, 0.35)];
      const y0 = b.y + b.h * (0.65 + rnd() * 0.2);
      const y1 = b.y + b.h * (0.15 + rnd() * 0.2);
      const bend = (rnd() - 0.5) * b.h * 0.5;
      const band = (t: number) => [b.x + b.w * t, y0 + (y1 - y0) * t + Math.sin(t * Math.PI) * bend] as const;
      const size = Math.min(b.w, b.h);
      const gauss = () => rnd() + rnd() - 1;
      for (let i = 0; i < Math.min(90, Math.max(14, Math.ceil(area / 30))); i++) {
        const [bx, by] = band(rnd());
        const r = size * (0.08 + rnd() * 0.14);
        g.circle(bx + gauss() * size * 0.1, by + gauss() * size * 0.22, r).fill({ color: tints[i % tints.length], alpha: 0.07 + rnd() * 0.08 });
      }
      for (let i = 0; i < Math.min(30, Math.max(4, Math.ceil(area / 120))); i++) {
        const [bx, by] = band(rnd());
        g.circle(bx + gauss() * size * 0.08, by + gauss() * size * 0.1, size * (0.03 + rnd() * 0.05)).fill({ color: lighten(neb, 0.6), alpha: 0.18 });
      }
      for (let i = 0; i < Math.min(16, Math.max(3, Math.ceil(area / 260))); i++) {
        const [bx, by] = band(rnd());
        g.circle(bx + gauss() * size * 0.12, by + gauss() * size * 0.1, size * (0.04 + rnd() * 0.06)).fill({ color: 0x05040e, alpha: 0.14 });
      }
      for (let i = 0; i < Math.min(260, Math.ceil(area / 9)); i++) {
        const big = rnd();
        g.circle(X(), Y(), 0.2 + big * big * big * 0.55).fill({ color: rnd() < 0.15 ? lighten(tints[1], 0.6) : 0xffffff, alpha: 0.45 + rnd() * 0.55 });
      }
      for (let i = 0; i < Math.min(14, Math.max(2, Math.ceil(area / 200))); i++) {
        const x = X();
        const y = Y();
        const r = 1.8 + rnd() * 1.8;
        g.circle(x, y, r * 0.8).fill({ color: lighten(neb, 0.5), alpha: 0.28 });
        sparkle(g, x, y, r, 0xffffff);
        g.circle(x, y, r * 0.2).fill(0xffffff);
      }
      break;
    }
    case 'mat:marbre': {
      // Marbre poli : nuages doux, veines sinueuses teintées, un fil doré.
      const vein = mix(darken(c, 0.35), 0x4a4a55, 0.35);
      for (let i = 0; i < Math.ceil(area / 120); i++) g.circle(X(), Y(), 3 + rnd() * 5).fill({ color: mix(0xcfcac2, c, 0.25), alpha: 0.1 });
      const n = Math.max(3, Math.ceil(area / 380));
      for (let k = 0; k < n; k++) {
        const gold = k === 0;
        let x = rnd() < 0.5 ? b.x - 1 : X();
        let y = x < b.x ? Y() : b.y - 1;
        let a = x < b.x ? (rnd() - 0.5) * 1.2 : Math.PI / 2 + (rnd() - 0.5) * 1.2;
        g.moveTo(x, y);
        const branch: [number, number, number][] = [];
        for (let s = 0; s < (b.w + b.h) / 1.5 && x >= b.x - 2 && x <= b.x + b.w + 2 && y >= b.y - 2 && y <= b.y + b.h + 2; s++) {
          a += (rnd() - 0.5) * 0.32;
          x += Math.cos(a) * 1.5;
          y += Math.sin(a) * 1.5;
          g.lineTo(x, y);
          if (!gold && rnd() < 0.06) branch.push([x, y, a + (rnd() < 0.5 ? 0.8 : -0.8)]);
        }
        if (gold) g.stroke({ width: 1.5, color: 0x8a6a1c, alpha: 0.45 }).stroke({ width: 0.8, color: 0xe6b84a, alpha: 1 });
        else g.stroke({ width: 1.8, color: vein, alpha: 0.12 }).stroke({ width: 0.5, color: vein, alpha: 0.75 });
        for (let [bx, by, ba] of branch) {
          g.moveTo(bx, by);
          for (let s = 0; s < 6; s++) {
            ba += (rnd() - 0.5) * 0.7;
            bx += Math.cos(ba) * 1.3;
            by += Math.sin(ba) * 1.3;
            g.lineTo(bx, by);
          }
          g.stroke({ width: 0.35, color: vein, alpha: 0.6 });
        }
      }
      g.poly([b.x + b.w * 0.15, b.y, b.x + b.w * 0.3, b.y, b.x + b.w * 0.1, b.y + b.h, b.x - b.w * 0.05, b.y + b.h]).fill({ color: 0xffffff, alpha: 0.28 });
      break;
    }
    case 'mat:rouille': {
      // Métal rouillé : restes de peinture (la couleur) écaillée sur acier nu, taches de rouille, piqûres.
      const rust = [0xc0621f, 0xa34d16, 0x7a3510, 0xd77a2e, 0x5a2a10];
      // Rouille marbrée (texture de fond).
      for (let i = 0; i < Math.ceil(area / 40); i++) g.poly(blob(rnd, X(), Y(), 1.5 + rnd() * 3.5, 8, 0.6)).fill({ color: rust[i % rust.length], alpha: 0.25 + rnd() * 0.3 });
      // Restes de peinture écaillée, bords très déchiquetés, liseré d'acier nu.
      for (let i = 0; i < Math.max(2, Math.ceil(area / 150)); i++) {
        const x = X();
        const y = Y();
        const r = 3 + rnd() * 5;
        g.poly(blob(rnd, x, y, r * 1.15, 16, 0.75, 1.4)).fill({ color: mix(0x8f8a84, c, 0.1), alpha: 0.9 });
        g.poly(blob(rnd, x, y, r, 16, 0.8, 1.4)).fill(darken(c, 0.08));
        g.poly(blob(rnd, x - r * 0.2, y - r * 0.3, r * 0.35, 7, 0.5, 1.4)).fill({ color: lighten(c, 0.2), alpha: 0.5 });
      }
      // Cloques de rouille qui percent la peinture.
      for (let i = 0; i < Math.ceil(area / 110); i++) {
        const x = X();
        const y = Y();
        const r = 0.8 + rnd() * 1.6;
        g.poly(blob(rnd, x, y, r, 8, 0.6)).fill({ color: rust[i % 2 ? 0 : 3], alpha: 0.85 });
        g.circle(x, y, r * 0.35).fill({ color: rust[2], alpha: 0.9 });
      }
      for (let i = 0; i < Math.min(220, Math.ceil(area / 14)); i++) {
        const x = X();
        const y = Y();
        const r = 0.25 + rnd() * 0.35;
        if (rnd() < 0.7) g.circle(x, y, r).fill({ color: 0x2a1408, alpha: 0.65 });
        else g.circle(x, y, r).fill({ color: 0xe08a3a, alpha: 0.55 });
      }
      break;
    }
    case 'mat:spectral': {
      // Fantomatique : voiles pâles ondulants de la couleur, poussière lumineuse (liseré diffus dans paintPart).
      const pale = lighten(vivid(c, 0.45, 0.55, 0.7), 0.45);
      for (let i = 0; i < Math.max(3, Math.ceil(b.h / 5)); i++) {
        const y = b.y + rnd() * b.h;
        const ph = rnd() * 6;
        const th = 1.2 + rnd() * 2.8;
        const top: number[] = [];
        const bot: number[] = [];
        for (let x = b.x - 2; x <= b.x + b.w + 2; x += 2) {
          const cy = y + Math.sin(x * 0.14 + ph) * 2.2;
          const w = th * (0.4 + 0.6 * Math.abs(Math.sin(x * 0.07 + ph * 2)));
          top.push(x, cy - w);
          bot.unshift(x, cy + w);
        }
        for (let k = 0; k < bot.length; k += 2) top.push(bot[k], bot[k + 1]);
        g.poly(top).fill({ color: pale, alpha: 0.14 + rnd() * 0.16 });
      }
      for (let i = 0; i < Math.max(2, Math.ceil(area / 260)); i++) g.circle(X(), Y(), 2 + rnd() * 3).fill({ color: 0x04050a, alpha: 0.22 });
      for (let i = 0; i < Math.min(80, Math.ceil(area / 45)); i++) g.circle(X(), Y(), 0.3 + rnd() * 0.4).fill({ color: pale, alpha: 0.35 + rnd() * 0.5 });
      break;
    }
    case 'mat:laque': {
      // Laque profonde : un fort reflet blanc net en haut, un second plus discret en bas (liseré doré dans paintPart).
      const lens = (x0: number, x1: number, yc: number, th: number, curve: number) => {
        const top: number[] = [];
        const bot: number[] = [];
        for (let k = 0; k <= 12; k++) {
          const t = k / 12;
          const x = x0 + (x1 - x0) * t;
          const y = yc - Math.sin(t * Math.PI) * curve;
          const w = Math.sin(t * Math.PI) * th;
          top.push(x, y - w);
          bot.unshift(x, y + w * 0.4);
        }
        for (let k = 0; k < bot.length; k += 2) top.push(bot[k], bot[k + 1]);
        return top;
      };
      g.poly(lens(b.x + b.w * 0.06, b.x + b.w * 0.7, b.y + b.h * 0.2, Math.min(b.h * 0.07, 2.2), b.h * 0.05)).fill({ color: 0xffffff, alpha: 0.8 });
      g.poly(lens(b.x + b.w * 0.5, b.x + b.w * 0.92, b.y + b.h * 0.82, Math.min(b.h * 0.035, 1.2), -b.h * 0.03)).fill({ color: 0xffffff, alpha: 0.35 });
      g.circle(b.x + b.w * 0.76, b.y + b.h * 0.17, Math.min(b.h * 0.04, 1.1)).fill({ color: 0xffffff, alpha: 0.85 });
      break;
    }
    case 'mat:gres': {
      // Grès : strates horizontales ondulées de teintes sable, grain fin.
      const k = mix(0xd4a76a, c, 0.25);
      const tints = [lighten(k, 0.18), darken(k, 0.1), mix(k, 0xb5643a, 0.35), lighten(k, 0.08), darken(k, 0.22), mix(k, 0xeed39c, 0.45)];
      const ph = rnd() * 6;
      const amp = 1.2 + rnd() * 0.8;
      const wave = (x: number, y: number) => y + Math.sin(x * 0.11 + ph) * amp + Math.sin(x * 0.29 + y * 0.2) * 0.35;
      for (let y = b.y - 3, i = 0; y < b.y + b.h + 3; i++) {
        const th = 1.2 + rnd() * 3;
        const pts: number[] = [];
        for (let x = b.x - 2; x <= b.x + b.w + 2; x += 2) pts.push(x, wave(x, y));
        for (let x = b.x + b.w + 2; x >= b.x - 2; x -= 2) pts.push(x, wave(x, y + th));
        g.poly(pts).fill({ color: tints[Math.floor(rnd() * tints.length)], alpha: 0.55 + rnd() * 0.3 });
        if (rnd() < 0.5) {
          g.moveTo(b.x - 2, wave(b.x - 2, y + th));
          for (let x = b.x; x <= b.x + b.w + 2; x += 2) g.lineTo(x, wave(x, y + th));
          g.stroke({ width: 0.4, color: darken(k, 0.4), alpha: 0.3 });
        }
        y += th;
      }
      for (let i = 0; i < Math.min(240, Math.ceil(area / 9)); i++) g.circle(X(), Y(), 0.22 + rnd() * 0.2).fill({ color: rnd() < 0.5 ? lighten(k, 0.4) : darken(k, 0.45), alpha: 0.35 });
      break;
    }
    case 'mat:porcelaine': {
      // Porcelaine : frises d'émail (filets, vague, points), petites fleurs entre les frises, reflet brillant.
      const blue = enamelOf(c);
      for (let y = b.y + 3.5; y < b.y + b.h + 2; y += 9) {
        g.moveTo(b.x, y - 1.8).lineTo(b.x + b.w, y - 1.8).moveTo(b.x, y + 1.8).lineTo(b.x + b.w, y + 1.8);
        g.stroke({ width: 0.45, color: blue, alpha: 0.9 });
        g.moveTo(b.x, y);
        for (let x = b.x; x <= b.x + b.w; x += 0.7) g.lineTo(x, y + Math.sin(((x - b.x) * Math.PI) / 2.2) * 0.95);
        g.stroke({ width: 0.45, color: blue, alpha: 0.9 });
        for (let x = b.x + 1.1; x <= b.x + b.w; x += 4.4) g.circle(x, y - 1.05, 0.32).circle(x + 2.2, y + 1.05, 0.32);
        g.fill({ color: blue, alpha: 0.9 });
        const yf = y + 4.5;
        for (let x = b.x + ((y - b.y) % 18 < 9 ? 2 : 5.5); x <= b.x + b.w; x += 7) {
          for (let k = 0; k < 4; k++) g.circle(x + Math.cos((k * Math.PI) / 2) * 0.8, yf + Math.sin((k * Math.PI) / 2) * 0.8, 0.5);
          g.fill({ color: blue, alpha: 0.75 });
          g.circle(x, yf, 0.35).fill({ color: darken(blue, 0.3), alpha: 0.9 });
        }
      }
      g.poly([b.x + b.w * 0.12, b.y, b.x + b.w * 0.26, b.y, b.x + b.w * 0.06, b.y + b.h, b.x - b.w * 0.08, b.y + b.h]).fill({ color: 0xffffff, alpha: 0.45 });
      g.poly([b.x + b.w * 0.32, b.y, b.x + b.w * 0.36, b.y, b.x + b.w * 0.16, b.y + b.h, b.x + b.w * 0.12, b.y + b.h]).fill({ color: 0xffffff, alpha: 0.3 });
      break;
    }
    case 'mat:paindepices': {
      // Pain d'épices : cuisson inégale (bords plus dorés), mie poreuse, grains d'épices et de sucre ; glaçage dans paintPart.
      const k = gingerOf(c);
      for (let i = 0; i < Math.ceil(area / 80); i++) g.poly(blob(rnd, X(), Y(), 2.5 + rnd() * 4, 9, 0.5)).fill({ color: rnd() < 0.5 ? darken(k, 0.2) : lighten(k, 0.14), alpha: 0.12 + rnd() * 0.1 });
      for (const m of b.rims ?? []) {
        rimPath(g, m).stroke({ width: 8, color: darken(k, 0.4), alpha: 0.14 });
        rimPath(g, m).stroke({ width: 4, color: darken(k, 0.45), alpha: 0.2 });
      }
      // Mie : pores sombres, chacun avec une lèvre claire en haut à gauche (relief du biscuit).
      const pores = Array.from({ length: Math.min(320, Math.ceil(area / 6)) }, () => [X(), Y(), 0.18 + rnd() * rnd() * 0.5]);
      for (const [x, y, r] of pores) g.circle(x - r * 0.4, y - r * 0.4, r * 0.9);
      g.fill({ color: lighten(k, 0.3), alpha: 0.32 });
      for (const [x, y, r] of pores) g.circle(x, y, r);
      g.fill({ color: darken(k, 0.5), alpha: 0.5 });
      for (let i = 0; i < Math.min(160, Math.ceil(area / 14)); i++) g.circle(X(), Y(), 0.15 + rnd() * 0.15);
      g.fill({ color: darken(k, 0.72), alpha: 0.55 });
      for (let i = 0; i < Math.min(60, Math.ceil(area / 60)); i++) g.circle(X(), Y(), 0.15 + rnd() * 0.18);
      g.fill({ color: 0xfff6e6, alpha: 0.6 });
      // Léger brillant de cuisson (dorure à l'œuf).
      g.poly([b.x + b.w * 0.14, b.y, b.x + b.w * 0.34, b.y, b.x + b.w * 0.12, b.y + b.h, b.x - b.w * 0.08, b.y + b.h]).fill({ color: 0xfff1d6, alpha: 0.09 });
      break;
    }
    case 'mat:velours': {
      // Velours : grand reflet diagonal très doux, lumière qui retombe vers le centre, poil fin ; fourrure dans paintPart.
      const v = velvetOf(c);
      const lite = lighten(v, 0.5);
      g.rect(b.x, b.y, b.w, b.h).fill(
        new FillGradient({
          type: 'linear',
          start: { x: 0, y: 0 },
          end: { x: 1, y: 0.6 },
          colorStops: [
            { offset: 0, color: rgba(lite, 0) },
            { offset: 0.28, color: rgba(lite, 0.17) },
            { offset: 0.42, color: rgba(lite, 0.04) },
            { offset: 0.62, color: rgba(lite, 0.11) },
            { offset: 0.8, color: rgba(lite, 0) },
            { offset: 1, color: rgba(lite, 0) },
          ],
          textureSpace: 'local',
        }),
      );
      // Chaque pièce : creux sombre au centre, poil éclairé de biais sur les bords (signature du velours).
      for (const m of b.rims ?? [])
        rimPath(g, m).fill(
          new FillGradient({
            type: 'radial',
            center: { x: 0.5, y: 0.5 },
            innerRadius: 0,
            outerCenter: { x: 0.5, y: 0.5 },
            outerRadius: 0.62,
            colorStops: [
              { offset: 0, color: rgba(0, 0.24) },
              { offset: 0.5, color: rgba(0, 0.1) },
              { offset: 0.78, color: rgba(lite, 0.03) },
              { offset: 1, color: rgba(lite, 0.2) },
            ],
            textureSpace: 'local',
          }),
        );
      // Velours écrasé : nuages clairs et sombres très doux.
      for (let i = 0; i < Math.ceil(area / 110); i++) g.poly(blob(rnd, X(), Y(), 3 + rnd() * 5, 9, 0.55)).fill({ color: rnd() < 0.5 ? lite : darken(v, 0.5), alpha: 0.05 + rnd() * 0.04 });
      // Poil : petits traits courts presque parallèles, clairs et sombres.
      const nap = Array.from({ length: Math.min(520, Math.ceil(area / 3)) }, () => {
        const a = 1.05 + (rnd() - 0.5) * 0.5;
        const l = 0.5 + rnd() * 0.7;
        const x = X();
        const y = Y();
        return [x, y, x + Math.cos(a) * l, y + Math.sin(a) * l];
      });
      for (const [tone, alpha, odd] of [[lite, 0.16, 0], [darken(v, 0.55), 0.22, 1]]) {
        nap.forEach(([x0, y0, x1, y1], i) => {
          if (i % 2 === odd) g.moveTo(x0, y0).lineTo(x1, y1);
        });
        g.stroke({ width: 0.3, color: tone, alpha });
      }
      break;
    }
    case 'mat:boulenoel': {
      // Verre de Noël : boule de sapin, couleur profonde, reflet miroir, fenêtre courbe, lumière renvoyée, paillettes d'or.
      const v = glassOf(c);
      if (b.rims) for (const m of b.rims) glassBall(g, m, v);
      else {
        // Tubes : reflets en diagonale comme le chrome (la boîte dépasse largement les tubes).
        g.poly([b.x + b.w * 0.12, b.y, b.x + b.w * 0.3, b.y, b.x + b.w * 0.1, b.y + b.h, b.x - b.w * 0.08, b.y + b.h]).fill({ color: 0xffffff, alpha: 0.3 });
        g.poly([b.x + b.w * 0.38, b.y, b.x + b.w * 0.42, b.y, b.x + b.w * 0.22, b.y + b.h, b.x + b.w * 0.18, b.y + b.h]).fill({ color: 0xffffff, alpha: 0.22 });
      }
      const gold = [0xe0a82e, 0xffd45a, 0xfff1b8];
      gold.forEach((col, k) => {
        for (let i = 0; i < Math.min(90, Math.ceil(area / 26)); i++) g.circle(X(), Y(), 0.14 + rnd() * rnd() * (0.42 - k * 0.1));
        g.fill({ color: col, alpha: 0.8 });
      });
      for (let i = 0; i < Math.min(10, Math.max(2, Math.ceil(area / 220))); i++) {
        const x = X();
        const y = Y();
        const r = 0.9 + rnd() * 0.9;
        g.circle(x, y, r * 0.55).fill({ color: 0xffd45a, alpha: 0.25 });
        sparkle(g, x, y, r, 0xffe9a0, 0.95);
        g.circle(x, y, r * 0.18).fill(0xffffff);
      }
      break;
    }
    default:
      break;
  }
}

/** Une pièce en boule de Noël : volume sphérique, fenêtre réfléchie courbe, point chaud, lumière renvoyée sur le bord opposé. */
function glassBall(g: Graphics, m: Rim, v: number): void {
  const W = 0xffffff;
  const rimLite = lighten(vivid(v, 0.8, 0.5, 0.7), 0.4);
  rimPath(g, m).fill(
    new FillGradient({
      type: 'radial',
      center: { x: 0.36, y: 0.3 },
      innerRadius: 0,
      outerCenter: { x: 0.5, y: 0.5 },
      outerRadius: 0.72,
      colorStops: [
        { offset: 0, color: rgba(lighten(v, 0.7), 0.5) },
        { offset: 0.3, color: rgba(lighten(v, 0.3), 0.08) },
        { offset: 0.64, color: rgba(v, 0) },
        { offset: 1, color: rgba(darken(v, 0.75), 0.62) },
      ],
      textureSpace: 'local',
    }),
  );
  if (isRound(m)) {
    // Dôme : la fenêtre suit la courbure de la sphère, dans l'anneau visible autour du cœur.
    const cx = m.x + m.w / 2;
    const cy = m.y + m.h / 2;
    const r = m.w / 2;
    const patch = (a0: number, a1: number, r0: number, r1: number) => {
      const pts: number[] = [];
      for (let k = 0; k <= 8; k++) {
        const a = a0 + ((a1 - a0) * k) / 8;
        pts.push(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
      }
      for (let k = 8; k >= 0; k--) {
        const a = a0 + ((a1 - a0) * k) / 8;
        pts.push(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
      }
      return pts;
    };
    // Lumière renvoyée en bas à droite.
    const arc = (rr: number, a0: number, a1: number) => {
      g.moveTo(cx + Math.cos(a0) * rr, cy + Math.sin(a0) * rr);
      for (let k = 1; k <= 16; k++) g.lineTo(cx + Math.cos(a0 + ((a1 - a0) * k) / 16) * rr, cy + Math.sin(a0 + ((a1 - a0) * k) / 16) * rr);
    };
    arc(r - 2, 0.15, 1.4);
    g.stroke({ width: 2.4, color: rimLite, alpha: 0.22, cap: 'round' });
    arc(r - 2, 0.25, 1.3);
    g.stroke({ width: 0.9, color: rimLite, alpha: 0.75, cap: 'round' });
    const [a0, a1, r0, r1] = [-2.95, -1.95, 0.68 * r, 0.9 * r];
    const am = (a0 + a1) / 2;
    const rm = (r0 + r1) / 2;
    const ga = 0.45 / rm;
    g.poly(patch(a0 - 0.08, a1 + 0.08, r0 - 0.5, r1 + 0.4)).fill({ color: W, alpha: 0.14 });
    g.poly(patch(a0, am - ga / 2, rm + 0.22, r1)).poly(patch(am + ga / 2, a1, rm + 0.22, r1)).fill({ color: W, alpha: 0.85 });
    g.poly(patch(a0, am - ga / 2, r0, rm - 0.22)).poly(patch(am + ga / 2, a1, r0, rm - 0.22)).fill({ color: W, alpha: 0.6 });
    const [hx, hy] = [cx + Math.cos(-1.55) * 0.8 * r, cy + Math.sin(-1.55) * 0.8 * r];
    g.circle(hx, hy, r * 0.06).fill({ color: W, alpha: 0.95 });
    sparkle(g, hx, hy, r * 0.15, W, 0.75);
    return;
  }
  const { x, y, w, h } = m;
  // Lumière renvoyée le long du bord bas.
  g.poly(lensPts(x + w * 0.2, x + w * 0.96, y + h - 2, Math.min(0.7, h * 0.06), -h * 0.02)).fill({ color: rimLite, alpha: 0.7 });
  // Fenêtre réfléchie : carreaux bombés en haut à gauche, puis une longue traînée de reflet.
  const [u0, u1, v0, v1] = [x + w * 0.07, x + w * 0.36, y + h * 0.17, y + h * 0.47];
  const bow = h * 0.06;
  const pane = (ua: number, ub: number, va: number, vb: number) => {
    const pts: number[] = [];
    const bend = (px: number) => bow * Math.sin(Math.PI * Math.min(1, Math.max(0, (px - u0) / (u1 - u0))));
    for (let k = 0; k <= 6; k++) {
      const px = ua + ((ub - ua) * k) / 6;
      pts.push(px, va - bend(px));
    }
    for (let k = 6; k >= 0; k--) {
      const px = ua + ((ub - ua) * k) / 6;
      pts.push(px, vb - bend(px));
    }
    return pts;
  };
  g.poly(pane(u0 - 0.7, u1 + 0.7, v0 - 0.6, v1 + 0.6)).fill({ color: W, alpha: 0.14 });
  const rows = h > 14 ? 2 : 1;
  const gap = 0.45;
  const cw = (u1 - u0 - gap * 2) / 3;
  const rh = (v1 - v0 - gap * (rows - 1)) / rows;
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < 3; i++) g.poly(pane(u0 + i * (cw + gap), u0 + i * (cw + gap) + cw, v0 + j * (rh + gap), v0 + j * (rh + gap) + rh));
    g.fill({ color: W, alpha: j === 0 ? 0.85 : 0.62 });
  }
  g.poly(lensPts(x + w * 0.42, x + w * 0.94, y + h * 0.25, Math.min(h * 0.07, 1.4), h * 0.03)).fill({ color: W, alpha: 0.55 });
  g.circle(x + w * 0.395, y + h * 0.22, Math.min(0.6, h * 0.06)).fill({ color: W, alpha: 0.95 });
}

/** Glaçage royal du pain d'épices : cordon blanc ondulé en relief le long des bords, perles de glace dans les creux. */
function icing(g: Graphics, rims: Rim[], k: number): void {
  const ICE = 0xfbf7ee;
  const shadow = darken(k, 0.6);
  for (const m of rims) {
    // Le dôme ne montre qu'un anneau autour du cœur (couleur du camp) : cordon plus près du bord.
    const d = isRound(m) ? 1.9 : Math.min(2.3, Math.min(m.w, m.h) * 0.24);
    const { pts, len } = rimTrace(m, d);
    if (pts.length < 3) continue;
    const lam = len / Math.max(3, Math.round(len / 3.2));
    const amp = 0.55;
    const wave = pts.map((p) => {
      const o = Math.sin((p.s / lam) * Math.PI * 2) * amp;
      return [p.x - p.nx * o, p.y - p.ny * o];
    });
    const cord = (dx: number, dy: number) => {
      g.moveTo(wave[0][0] + dx, wave[0][1] + dy);
      for (const [px, py] of wave.slice(1)) g.lineTo(px + dx, py + dy);
      g.closePath();
    };
    cord(0.35, 0.45);
    g.stroke({ width: 1.25, color: shadow, alpha: 0.45, join: 'round' });
    cord(0, 0);
    g.stroke({ width: 0.95, color: ICE, join: 'round' });
    cord(-0.15, -0.18);
    g.stroke({ width: 0.3, color: 0xffffff, alpha: 0.9, join: 'round' });
    // Perles dans chaque creux de l'onde (vers l'intérieur de la pièce).
    const beads: [number, number][] = [];
    let next = lam * 0.25;
    for (const p of pts) {
      if (p.s < next) continue;
      beads.push([p.x - p.nx * (amp + 1), p.y - p.ny * (amp + 1)]);
      next += lam;
    }
    for (const [bx, by] of beads) g.circle(bx + 0.3, by + 0.38, 0.5);
    g.fill({ color: shadow, alpha: 0.45 });
    for (const [bx, by] of beads) g.circle(bx, by, 0.48);
    g.fill(ICE);
    for (const [bx, by] of beads) g.circle(bx - 0.15, by - 0.17, 0.16);
    g.fill(0xffffff);
  }
}

/** Fourrure blanche du velours (costume du père Noël) : bande duveteuse le long des bords, ombre portée sur le velours. */
function furTrim(g: Graphics, rims: Rim[], v: number, seed: number): void {
  const rnd = seeded(seed * 13 + 5);
  for (const m of rims) {
    // Bande fine (le contour sombre en couvre déjà 1,25) ; plus fine encore sur l'anneau visible du dôme.
    const d = isRound(m) ? 1.9 : Math.min(2.4, Math.min(m.w, m.h) * 0.24);
    rimPath(g, m, d + 0.6).stroke({ width: 1.8, color: darken(v, 0.65), alpha: 0.4 });
    rimPath(g, m).stroke({ width: d * 2, color: 0xe6e0d6 });
    // Bord intérieur duveteux : petites touffes rondes qui débordent sur le velours.
    for (const p of rimTrace(m, d, 0.85).pts) g.circle(p.x + (rnd() - 0.5) * 0.4, p.y + (rnd() - 0.5) * 0.4, 0.42 + rnd() * 0.34);
    g.fill(0xf1ede6);
    const mid = rimTrace(m, d * 0.72, 1.05).pts;
    for (const p of mid) g.circle(p.x + (rnd() - 0.5) * 1.2, p.y + (rnd() - 0.5) * 1.2, 0.22 + rnd() * 0.25);
    g.fill({ color: 0xb9b1a5, alpha: 0.45 });
    for (const p of mid) g.circle(p.x + (rnd() - 0.5) * 1.2, p.y + (rnd() - 0.5) * 1.2, 0.18 + rnd() * 0.2);
    g.fill({ color: 0xffffff, alpha: 0.9 });
  }
}

// ─── Motifs ───────────────────────────────────────────────────────────

function patternOverlay(g: Graphics, p: PartPaint, b: Box, seed: number, barrels = false): void {
  if (!p.pat) return;
  const col = p.patColor;
  const rnd = seeded(seed * 7 + 3);
  const a = 0.85;
  switch (p.pat) {
    case 'pat:rayures':
      for (let x = b.x - b.h; x < b.x + b.w; x += 5.5) g.poly([x, b.y + b.h, x + 2.6, b.y + b.h, x + 2.6 + b.h, b.y, x + b.h, b.y]).fill({ color: col, alpha: a });
      break;
    case 'pat:damier': {
      const s = 4;
      for (let x = b.x; x < b.x + b.w; x += s) for (let y = b.y; y < b.y + b.h; y += s) if ((Math.round((x - b.x) / s) + Math.round((y - b.y) / s)) % 2 === 0) g.rect(x, y, s, s).fill({ color: col, alpha: a });
      break;
    }
    case 'pat:camo':
      for (let i = 0; i < Math.ceil((b.w * b.h) / 70); i++) {
        const x = b.x + rnd() * b.w;
        const y = b.y + rnd() * b.h;
        const r = 2.5 + rnd() * 4;
        const pts: number[] = [];
        for (let k = 0; k < 7; k++) {
          const t = (k / 7) * Math.PI * 2;
          const rr = r * (0.6 + rnd() * 0.7);
          pts.push(x + Math.cos(t) * rr * 1.4, y + Math.sin(t) * rr);
        }
        g.poly(pts).fill({ color: i % 2 ? col : mix(col, 0x000000, 0.35), alpha: 0.9 });
      }
      break;
    case 'pat:hexa': {
      const s = 3.2;
      for (let row = 0, y = b.y; y < b.y + b.h + s; row++, y += s * 1.5) {
        for (let x = b.x + (row % 2 ? s * 0.87 : 0); x < b.x + b.w + s; x += s * 1.73) g.regularPoly(x, y, s * 0.92, 6).stroke({ width: 0.8, color: col, alpha: a });
      }
      break;
    }
    case 'pat:ecailles': {
      const s = 3.6;
      for (let row = 0, y = b.y; y < b.y + b.h + s; row++, y += s * 0.75) {
        for (let x = b.x + (row % 2 ? s / 2 : 0); x < b.x + b.w + s; x += s) g.arc(x, y, s / 2, 0, Math.PI).stroke({ width: 0.9, color: col, alpha: a });
      }
      break;
    }
    case 'pat:circuit':
      for (let i = 0; i < Math.ceil((b.w * b.h) / 55); i++) {
        let x = b.x + Math.round((rnd() * b.w) / 2.5) * 2.5;
        let y = b.y + Math.round((rnd() * b.h) / 2.5) * 2.5;
        g.moveTo(x, y);
        for (let s = 0; s < 3; s++) {
          if (rnd() < 0.5) x += (rnd() < 0.5 ? -1 : 1) * 5;
          else y += (rnd() < 0.5 ? -1 : 1) * 5;
          g.lineTo(x, y);
        }
        g.stroke({ width: 0.8, color: col, alpha: a });
        g.circle(x, y, 0.9).fill({ color: col, alpha: 1 });
      }
      break;
    case 'pat:tigre':
      for (let x = b.x + 2; x < b.x + b.w; x += 4.5 + rnd() * 2) {
        const w = 1.2 + rnd() * 1.4;
        const top = rnd() < 0.5;
        const len = b.h * (0.35 + rnd() * 0.3);
        const y0 = top ? b.y : b.y + b.h;
        const dir = top ? 1 : -1;
        g.poly([x - w, y0, x + w, y0, x + w * 0.2 + dir * 0, y0 + dir * len, x - w * 0.3, y0 + dir * len * 0.9]).fill({ color: col, alpha: a });
      }
      break;
    case 'pat:flammes': {
      // Flammes peintes : langues depuis l'arrière (−x), vers l'avant.
      for (let i = 0; i < 5; i++) {
        const y = b.y + (b.h * (i + 0.5)) / 5;
        const len = b.w * (0.45 + rnd() * 0.35);
        const w = b.h / 5;
        g.moveTo(b.x - 2, y - w * 0.8)
          .bezierCurveTo(b.x + len * 0.4, y - w * 0.9, b.x + len * 0.7, y - w * 0.2, b.x + len, y)
          .bezierCurveTo(b.x + len * 0.7, y + w * 0.3, b.x + len * 0.4, y + w * 0.9, b.x - 2, y + w * 0.8)
          .fill({ color: col, alpha: 0.9 });
        g.moveTo(b.x - 2, y - w * 0.35)
          .bezierCurveTo(b.x + len * 0.3, y - w * 0.4, b.x + len * 0.5, y, b.x + len * 0.7, y)
          .bezierCurveTo(b.x + len * 0.5, y + w * 0.1, b.x + len * 0.3, y + w * 0.4, b.x - 2, y + w * 0.35)
          .fill({ color: lighten(mix(col, 0xffe066, 0.6), 0.3), alpha: 0.9 });
      }
      break;
    }
    default:
      themedPattern(g, p.pat, col, b, rnd, barrels);
      break;
  }
}

/** Motifs des collections à thème. */
/** `barrels` : boîte des tubes, bien plus grande que les tubes eux-mêmes. */
function themedPattern(g: Graphics, pat: string, col: number, b: Box, rnd: () => number, barrels: boolean): void {
  const a = 0.85;
  const area = b.w * b.h;
  const X = () => b.x + rnd() * b.w;
  const Y = () => b.y + rnd() * b.h;
  switch (pat) {
    case 'pat:givre': {
      // Cristaux de givre ramifiés qui partent des bords vers l'intérieur.
      const segs: number[][][] = [[], [], []];
      const grow = (x: number, y: number, ang: number, len: number, depth: number) => {
        const ex = x + Math.cos(ang) * len;
        const ey = y + Math.sin(ang) * len;
        segs[2 - depth].push([x, y, ex, ey]);
        if (depth === 0) return;
        const ts = depth === 2 ? [0.3, 0.55, 0.78] : [0.4, 0.72];
        for (const t of ts) {
          const bx = x + (ex - x) * t;
          const by = y + (ey - y) * t;
          for (const side of [-1, 1]) grow(bx, by, ang + side * (0.95 + rnd() * 0.2), len * (1 - t) * (depth === 2 ? 0.62 : 0.55), depth - 1);
        }
      };
      const per = 2 * (b.w + b.h);
      const m = Math.min(b.w, b.h);
      const n = Math.max(6, Math.round(per / Math.max(8, m * 0.28)));
      const len = () => Math.max(5, m * (0.2 + rnd() * 0.16));
      for (let i = 0; i < n; i++) {
        let d = ((i + rnd() * 0.8) / n) * per;
        let x: number, y: number, ang: number;
        if (d < b.w) [x, y, ang] = [b.x + d, b.y, Math.PI / 2];
        else if ((d -= b.w) < b.h) [x, y, ang] = [b.x + b.w, b.y + d, Math.PI];
        else if ((d -= b.h) < b.w) [x, y, ang] = [b.x + b.w - d, b.y + b.h, -Math.PI / 2];
        else [x, y, ang] = [b.x, b.y + b.h - (d - b.w), 0];
        grow(x, y, ang + (rnd() - 0.5) * 0.9, len(), 2);
      }
      // Grandes boîtes (tubes) : les bords de la boîte sont loin des pièces, on sème aussi du givre à l'intérieur.
      if (barrels) for (let i = 0; i < Math.ceil(area / 220); i++) grow(X(), Y(), rnd() * Math.PI * 2, 5 + rnd() * 4, 2);
      segs.forEach((list, depth) => {
        for (const [x0, y0, x1, y1] of list) g.moveTo(x0, y0).lineTo(x1, y1);
        g.stroke({ width: [1, 0.65, 0.4][depth], color: col, alpha: a, cap: 'round' });
      });
      break;
    }
    case 'pat:lave': {
      // Coulées de lave : rubans sinueux à croûte sombre et cœur clair.
      const n = Math.max(2, Math.round(b.h / 8));
      const hot = lighten(mix(col, 0xffe066, 0.55), 0.3);
      for (let i = 0; i < n; i++) {
        const y0 = b.y + (b.h * (i + 0.5)) / n + (rnd() - 0.5) * 3;
        const ph = rnd() * 6;
        const f = 0.1 + rnd() * 0.1;
        const A = 1.2 + rnd() * 2.2;
        const w0 = 1.3 + rnd() * 1.3;
        const ribbon = (k: number) => {
          const top: number[] = [];
          const bot: number[] = [];
          for (let x = b.x - 3; x <= b.x + b.w + 3; x += 2) {
            const cy = y0 + Math.sin(x * f + ph) * A + Math.sin(x * f * 2.3 + ph * 1.7) * A * 0.35;
            const w = w0 * k * (0.6 + 0.55 * (0.5 + 0.5 * Math.sin(x * 0.23 + ph * 2)));
            top.push(x, cy - w);
            bot.unshift(x, cy + w);
          }
          for (let j = 0; j < bot.length; j += 2) top.push(bot[j], bot[j + 1]);
          return top;
        };
        g.poly(ribbon(1.3)).fill({ color: darken(col, 0.45), alpha: 0.9 });
        g.poly(ribbon(1)).fill({ color: col, alpha: 0.95 });
        g.poly(ribbon(0.4)).fill({ color: hot, alpha: 0.95 });
      }
      for (let i = 0; i < Math.ceil(area / 90); i++) g.circle(X(), Y(), 0.4 + rnd() * 0.6).fill({ color: rnd() < 0.5 ? col : hot, alpha: 0.9 });
      break;
    }
    case 'pat:pixels': {
      // Pixels en grille, denses à l'arrière et de plus en plus épars vers l'avant (dégradé pixelisé).
      const s = 2.6;
      for (let x = b.x; x < b.x + b.w; x += s) {
        const t = (x - b.x) / b.w;
        for (let y = b.y; y < b.y + b.h; y += s) {
          if (rnd() > 1.05 - t * 1.15) continue;
          const r = rnd();
          g.rect(x + 0.2, y + 0.2, s - 0.4, s - 0.4).fill({ color: r < 0.18 ? lighten(col, 0.35) : r < 0.3 ? darken(col, 0.3) : col, alpha: 0.9 });
        }
      }
      break;
    }
    case 'pat:cascade': {
      // Code qui tombe : colonnes de petits glyphes, plus brillants en bas de chaque colonne.
      const cw = 2.6;
      const gh = 1.9;
      const head = lighten(col, 0.7);
      for (let x = b.x + 0.4; x < b.x + b.w; x += cw) {
        const drops = rnd() < 0.6 ? 2 : 1;
        for (let d = 0; d < drops; d++) {
          const len = 4 + Math.floor(rnd() * 8);
          const yEnd = b.y + rnd() * (b.h + len * gh * 0.5);
          for (let k = 0; k < len; k++) {
            const y = yEnd - k * gh;
            if (y < b.y - gh || y > b.y + b.h) continue;
            const fill = { color: k === 0 ? head : col, alpha: k === 0 ? 1 : 0.15 + 0.75 * (1 - k / len) };
            switch (Math.floor(rnd() * 4)) {
              case 0:
                g.rect(x, y, 1.6, 1.3).fill(fill);
                break;
              case 1:
                g.rect(x, y, 0.6, 1.3).rect(x + 1, y, 0.6, 1.3).fill(fill);
                break;
              case 2:
                g.rect(x, y, 1.6, 0.55).rect(x + 0.5, y + 0.75, 0.6, 0.55).fill(fill);
                break;
              default:
                g.rect(x + 0.5, y, 0.6, 1.3).rect(x, y + 0.4, 1.6, 0.5).fill(fill);
            }
          }
        }
      }
      break;
    }
    case 'pat:feuillage': {
      // Feuilles superposées (ellipses pointues à nervure), sur une grille irrégulière.
      const shades = [col, darken(col, 0.22), lighten(col, 0.15)];
      const sp = 5;
      for (let gy = b.y - 2; gy < b.y + b.h + 2; gy += sp) {
        for (let gx = b.x - 2; gx < b.x + b.w + 2; gx += sp) {
          const x = gx + rnd() * sp;
          const y = gy + rnd() * sp;
          const ang = rnd() * Math.PI * 2;
          const L = 3.2 + rnd() * 2.2;
          const W = L * 0.42;
          const cx = Math.cos(ang);
          const sy = Math.sin(ang);
          const [tx, ty, bx, by] = [x + cx * L, y + sy * L, x - cx * L, y - sy * L];
          g.moveTo(bx, by)
            .quadraticCurveTo(x - sy * W * 2, y + cx * W * 2, tx, ty)
            .quadraticCurveTo(x + sy * W * 2, y - cx * W * 2, bx, by)
            .fill({ color: shades[Math.floor(rnd() * 3)], alpha: 0.95 })
            .stroke({ width: 0.4, color: darken(col, 0.5), alpha: 0.8 });
          g.moveTo(bx - cx * 0.8, by - sy * 0.8).lineTo(x + cx * L * 0.75, y + sy * L * 0.75).stroke({ width: 0.35, color: darken(col, 0.45), alpha: 0.85 });
        }
      }
      break;
    }
    case 'pat:courants': {
      // Courants marins : lignes ondulées parallèles et fluides, une sur trois plus marquée.
      const ph = rnd() * 6;
      for (let y0 = b.y - 5, i = 0; y0 < b.y + b.h + 5; y0 += 3.2, i++) {
        const at = (x: number) => y0 + Math.sin(x * 0.15 + ph + y0 * 0.05) * 1.5 + Math.sin(x * 0.05 + ph * 0.5) * 2.4;
        g.moveTo(b.x - 1, at(b.x - 1));
        for (let x = b.x + 0.5; x <= b.x + b.w + 2; x += 1.5) g.lineTo(x, at(x));
        const strong = i % 3 === 0;
        g.stroke({ width: strong ? 1.1 : 0.5, color: col, alpha: strong ? 0.9 : 0.55, cap: 'round' });
      }
      break;
    }
    case 'pat:constellations': {
      // Groupes d'étoiles reliées par des traits fins (chemin du plus proche voisin).
      for (let c = 0; c < Math.max(2, Math.ceil(area / 190)); c++) {
        const cx = X();
        const cy = Y();
        const pts: [number, number][] = Array.from({ length: 3 + Math.floor(rnd() * 3) }, () => [cx + (rnd() - 0.5) * 16, cy + (rnd() - 0.5) * 12]);
        const path = [pts.shift()!];
        while (pts.length) {
          const [lx, ly] = path[path.length - 1];
          let best = 0;
          pts.forEach(([px, py], i) => {
            if ((px - lx) ** 2 + (py - ly) ** 2 < (pts[best][0] - lx) ** 2 + (pts[best][1] - ly) ** 2) best = i;
          });
          path.push(pts.splice(best, 1)[0]);
        }
        g.moveTo(path[0][0], path[0][1]);
        for (const [px, py] of path.slice(1)) g.lineTo(px, py);
        g.stroke({ width: 0.4, color: col, alpha: 0.6 });
        path.forEach(([px, py], i) => {
          const r = i === 0 ? 1.7 : 0.9 + rnd() * 0.7;
          sparkle(g, px, py, r, col, 0.95);
          g.circle(px, py, r * 0.32).fill({ color: lighten(col, 0.5), alpha: 1 });
        });
      }
      for (let i = 0; i < Math.ceil(area / 40); i++) g.circle(X(), Y(), 0.3).fill({ color: col, alpha: 0.5 });
      break;
    }
    case 'pat:losanges': {
      // Argyle : losanges en damier (deux tons) et fines lignes diagonales croisées.
      const w = 6;
      const h = 8.5;
      for (let j = 0; b.y + j * h <= b.y + b.h + h; j++) {
        for (let i = 0; b.x + i * w <= b.x + b.w + w; i++) {
          const cx = b.x + i * w;
          const cy = b.y + j * h;
          g.poly([cx, cy - h / 2, cx + w / 2, cy, cx, cy + h / 2, cx - w / 2, cy]).fill({ color: (i + j) % 2 ? col : darken(col, 0.3), alpha: a });
        }
      }
      for (let k = -Math.ceil(b.h / h) - 2; k <= Math.ceil(b.w / w) + 2; k++) {
        const x0 = b.x + k * w;
        const dy = b.h + h * 2;
        g.moveTo(x0 - (w / h) * h, b.y - h).lineTo(x0 + (w / h) * (dy - h), b.y + b.h + h);
        g.moveTo(x0 + (w / h) * h, b.y - h).lineTo(x0 - (w / h) * (dy - h), b.y + b.h + h);
      }
      g.stroke({ width: 0.45, color: lighten(col, 0.5), alpha: 0.9 });
      break;
    }
    case 'pat:plaques': {
      // Plaques rivetées en quinconce : biseau clair en haut, sombre en bas, rivets aux coins.
      const pw = 9;
      const ph = 6.2;
      for (let row = 0, y = b.y - 1; y < b.y + b.h; y += ph, row++) {
        for (let x = b.x - 1 - (row % 2 ? pw / 2 : 0); x < b.x + b.w; x += pw) {
          const t = rnd();
          g.rect(x + 0.35, y + 0.35, pw - 0.7, ph - 0.7).fill({ color: t < 0.33 ? col : t < 0.66 ? darken(col, 0.12) : lighten(col, 0.08), alpha: 0.75 });
        }
      }
      for (let row = 0, y = b.y - 1; y < b.y + b.h; y += ph, row++) for (let x = b.x - 1 - (row % 2 ? pw / 2 : 0); x < b.x + b.w; x += pw) g.moveTo(x + 0.6, y + 0.7).lineTo(x + pw - 0.6, y + 0.7);
      g.stroke({ width: 0.45, color: lighten(col, 0.45), alpha: 0.8 });
      for (let row = 0, y = b.y - 1; y < b.y + b.h; y += ph, row++) for (let x = b.x - 1 - (row % 2 ? pw / 2 : 0); x < b.x + b.w; x += pw) g.moveTo(x + 0.6, y + ph - 0.7).lineTo(x + pw - 0.6, y + ph - 0.7);
      g.stroke({ width: 0.45, color: darken(col, 0.55), alpha: 0.8 });
      for (let row = 0, y = b.y - 1; y < b.y + b.h; y += ph, row++) {
        for (let x = b.x - 1 - (row % 2 ? pw / 2 : 0); x < b.x + b.w; x += pw) for (const [rx, ry] of [[1.4, 1.4], [pw - 1.4, 1.4], [1.4, ph - 1.4], [pw - 1.4, ph - 1.4]]) g.circle(x + rx, y + ry, 0.55);
      }
      g.fill({ color: darken(col, 0.5), alpha: 0.9 });
      for (let row = 0, y = b.y - 1; y < b.y + b.h; y += ph, row++) {
        for (let x = b.x - 1 - (row % 2 ? pw / 2 : 0); x < b.x + b.w; x += pw) for (const [rx, ry] of [[1.4, 1.4], [pw - 1.4, 1.4], [1.4, ph - 1.4], [pw - 1.4, ph - 1.4]]) g.circle(x + rx - 0.15, y + ry - 0.15, 0.3);
      }
      g.fill({ color: lighten(col, 0.55), alpha: 0.95 });
      break;
    }
    case 'pat:toiles': {
      // Toiles d'araignée : rayons depuis un coin, spirale polygonale légèrement affaissée.
      const corners: [number, number, number][] = [
        [b.x, b.y, 0],
        [b.x + b.w, b.y + b.h, Math.PI],
      ];
      if (area > 1500) corners.push([b.x + b.w, b.y, Math.PI / 2], [b.x, b.y + b.h, -Math.PI / 2]);
      const L = Math.min(b.w, b.h) * (area > 1500 ? 0.6 : 0.85);
      for (const [cx, cy, a0] of corners) {
        const nr = 7;
        const rays = Array.from({ length: nr }, (_, k) => a0 + ((k + 0.5) / nr) * (Math.PI / 2) + (rnd() - 0.5) * 0.12);
        for (const t of rays) g.moveTo(cx, cy).lineTo(cx + Math.cos(t) * L, cy + Math.sin(t) * L);
        g.stroke({ width: 0.5, color: col, alpha: 0.9 });
        for (let r = 2.4; r < L * 0.95; r += 2.1 + r * 0.06) {
          g.moveTo(cx + Math.cos(rays[0]) * r, cy + Math.sin(rays[0]) * r);
          for (let k = 1; k < nr; k++) {
            const m = (rays[k - 1] + rays[k]) / 2;
            g.quadraticCurveTo(cx + Math.cos(m) * r * 0.86, cy + Math.sin(m) * r * 0.86, cx + Math.cos(rays[k]) * r, cy + Math.sin(rays[k]) * r);
          }
        }
        g.stroke({ width: 0.38, color: col, alpha: 0.85 });
      }
      break;
    }
    case 'pat:seigaiha': {
      // Vagues seigaiha : éventails d'arcs concentriques ; chaque éventail est caché par ceux de devant.
      const r = 4.2;
      const sx = 2 * r;
      const vy = r * 0.55;
      const cx0 = b.x - r;
      const cy0 = b.y - r * 0.5;
      const at = (i: number, j: number): [number, number] => [cx0 + i * sx + (j % 2 ? r : 0), cy0 + j * vy];
      const covered = (px: number, py: number, i: number, j: number) => {
        for (let dj = 1; dj <= 4; dj++) {
          for (let di = -1; di <= 1; di++) {
            const [fx, fy] = at(i + di, j + dj);
            if ((px - fx) ** 2 + (py - fy) ** 2 < (r * 0.99) ** 2) return true;
          }
        }
        return false;
      };
      const rows = Math.ceil((b.h + r * 2) / vy) + 1;
      const cols = Math.ceil((b.w + r * 2) / sx) + 1;
      const rings = [0.95, 0.7, 0.45];
      rings.forEach((k, ri) => {
        for (let j = 0; j < rows; j++) {
          for (let i = 0; i < cols; i++) {
            const [cx, cy] = at(i, j);
            let prev = false;
            for (let s = 0; s <= 32; s++) {
              const t = (s / 32) * Math.PI * 2;
              const px = cx + Math.cos(t) * r * k;
              const py = cy + Math.sin(t) * r * k;
              const vis = !covered(px, py, i, j);
              if (vis) {
                if (prev) g.lineTo(px, py);
                else g.moveTo(px, py);
              }
              prev = vis;
            }
          }
        }
        g.stroke({ width: ri === 0 ? 0.85 : 0.55, color: col, alpha: a });
      });
      break;
    }
    case 'pat:dunes': {
      // Dunes larges et douces : versant ombré sous chaque crête, versant éclairé, crête claire.
      const S = 8;
      const slope = 0.18;
      const ph = rnd() * 6;
      const crest = (k: number, x: number) => b.y + k * S + Math.sin(x * 0.085 + ph + k * 0.7) * 2.2 - (x - b.x) * slope;
      const shadow = (k: number, x: number) => crest(k, x) + S * 0.42 + Math.sin(x * 0.13 + k * 1.3) * 0.8;
      const xs: number[] = [];
      for (let x = b.x - 2; x <= b.x + b.w + 2; x += 2) xs.push(x);
      const band = (f0: (x: number) => number, f1: (x: number) => number) => {
        const pts: number[] = [];
        for (const x of xs) pts.push(x, f0(x));
        for (let i = xs.length - 1; i >= 0; i--) pts.push(xs[i], f1(xs[i]));
        return pts;
      };
      for (let k = -1; k <= Math.ceil((b.h + b.w * slope) / S) + 1; k++) {
        g.poly(band((x) => crest(k, x), (x) => shadow(k, x))).fill({ color: darken(col, 0.32), alpha: 0.6 });
        g.poly(band((x) => shadow(k, x), (x) => crest(k + 1, x))).fill({ color: col, alpha: 0.32 });
        g.moveTo(xs[0], crest(k, xs[0]));
        for (const x of xs) g.lineTo(x, crest(k, x));
        g.stroke({ width: 0.6, color: lighten(col, 0.35), alpha: 0.85 });
      }
      break;
    }
    case 'pat:plumes': {
      // Plumes en écailles (pointe vers l'arrière) : rachis clair, barbes, de l'arrière vers l'avant.
      const L = 10;
      const W = 3.4;
      const dx = 6.5;
      const dy = 5;
      const shades = [col, darken(col, 0.15), lighten(col, 0.1)];
      for (let ci = 0, x = b.x - L * 0.3; x < b.x + b.w + L * 0.6; x += dx, ci++) {
        for (let y = b.y - dy + (ci % 2 ? dy / 2 : 0); y < b.y + b.h + dy; y += dy) {
          const bx = x + L * 0.45;
          const tx = x - L * 0.55;
          const ty = y + (rnd() - 0.5) * 0.8;
          g.moveTo(bx, y)
            .bezierCurveTo(x + L * 0.2, y - W, x - L * 0.3, y - W * 0.9, tx, ty)
            .bezierCurveTo(x - L * 0.3, y + W * 0.9, x + L * 0.2, y + W, bx, y)
            .fill({ color: shades[Math.floor(rnd() * 3)], alpha: 0.92 })
            .stroke({ width: 0.35, color: darken(col, 0.5), alpha: 0.85 });
          for (let t = 0.2; t < 0.9; t += 0.14) {
            const px = bx - t * (bx - tx);
            const py = y + (ty - y) * t;
            const hw = Math.sin(Math.PI * t) * W * 0.62;
            g.moveTo(px, py).lineTo(px - 1.6, py - hw).moveTo(px, py).lineTo(px - 1.6, py + hw);
          }
          g.stroke({ width: 0.3, color: darken(col, 0.35), alpha: 0.75 });
          g.moveTo(bx + 0.6, y).lineTo(tx + 0.8, ty).stroke({ width: 0.45, color: lighten(col, 0.45), alpha: 0.9 });
        }
      }
      break;
    }
    case 'pat:sucredorge': {
      // Sucre d'orge : larges rayures en diagonale, un filet fin entre deux, arête claire, reflet de sucre glacé.
      const P = 7.5;
      const W = 2.8;
      const w2 = 0.8;
      const stripe = (x0: number, w: number) => g.poly([x0, b.y + b.h, x0 + w, b.y + b.h, x0 + w + b.h, b.y, x0 + b.h, b.y]);
      for (let x = b.x - b.h; x < b.x + b.w + P; x += P) {
        stripe(x, W);
        stripe(x + W + (P - W - w2) / 2, w2);
      }
      g.fill({ color: col, alpha: 0.92 });
      for (let x = b.x - b.h; x < b.x + b.w + P; x += P) g.moveTo(x + W - 0.4, b.y + b.h).lineTo(x + W - 0.4 + b.h, b.y);
      g.stroke({ width: 0.45, color: lighten(col, 0.5), alpha: 0.6 });
      for (let x = b.x - b.h; x < b.x + b.w + P; x += P) g.moveTo(x + 0.3, b.y + b.h).lineTo(x + 0.3 + b.h, b.y);
      g.stroke({ width: 0.4, color: darken(col, 0.45), alpha: 0.5 });
      for (const m of b.rims ?? []) {
        g.poly(lensPts(m.x + m.w * 0.05, m.x + m.w * 0.95, m.y + m.h * 0.26, Math.min(m.h * 0.08, 1.5), m.h * 0.03)).fill({ color: 0xffffff, alpha: 0.32 });
        g.poly(lensPts(m.x + m.w * 0.3, m.x + m.w * 0.85, m.y + m.h * 0.8, Math.min(m.h * 0.04, 0.8), -m.h * 0.02)).fill({ color: 0xffffff, alpha: 0.16 });
      }
      break;
    }
    case 'pat:tricot': {
      // Pull de Noël : mailles en V sur toute la pièce ; au centre une rangée de flocons, puis zigzags et bande pleine,
      // en miroir (jacquard nordique). La grille est centrée sur chaque pièce pour que les flocons tombent au milieu.
      const cw = 1.5;
      const chh = 1.3;
      const FLAKE = ['XX.XX', '.XXX.', 'X.X.X'];
      const mod = (n: number, k: number) => ((n % k) + k) % k;
      const on = (i: number, k: number) => {
        const r = Math.abs(k) % 16;
        const kk = r > 8 ? 16 - r : r;
        if (kk <= 2) {
          const cc = mod(i + 2, 8);
          return cc < 5 ? FLAKE[kk][cc] === 'X' : kk === 0 && cc === 6;
        }
        if (kk === 4) return mod(i, 4) === 0;
        if (kk === 5) return mod(i, 2) === 1;
        if (kk === 6) return mod(i, 4) === 2;
        return kk === 8;
      };
      for (const m of b.rims ?? [{ ...b, r: 0 }]) {
        const cx = m.x + m.w / 2;
        const cy = m.y + m.h / 2;
        const ni = Math.ceil(m.w / 2 / cw) + 1;
        const nk = Math.ceil(m.h / 2 / chh) + 1;
        const cells: [number, number, boolean][] = [];
        for (let k = -nk; k <= nk; k++) for (let i = -ni; i <= ni; i++) cells.push([cx + i * cw - cw / 2, cy + k * chh - chh / 2, on(i, k)]);
        for (const [x, y, lit] of cells) if (lit) g.poly([x, y, x + cw / 2, y + chh * 0.42, x + cw, y, x + cw, y + chh * 0.58, x + cw / 2, y + chh, x, y + chh * 0.58]);
        g.fill({ color: col, alpha: 0.95 });
        for (const [x, y, lit] of cells) if (lit) g.moveTo(x + cw * 0.14, y + chh * 0.2).lineTo(x + cw * 0.46, y + chh * 0.72);
        g.stroke({ width: 0.3, color: lighten(col, 0.4), alpha: 0.55 });
        // Texture de maille sur toute la pièce (fond compris).
        for (const [x, y] of cells) g.moveTo(x + cw * 0.08, y + chh * 0.12).lineTo(x + cw / 2, y + chh * 0.9).lineTo(x + cw * 0.92, y + chh * 0.12);
        g.stroke({ width: 0.3, color: 0x000000, alpha: 0.2, join: 'round' });
      }
      break;
    }
    case 'pat:emballage': {
      // Papier cadeau : semis d'étoiles, de petits paquets et de points, puis un ruban croisé avec son nœud sur chaque pièce.
      const sp = 6.2;
      const gifts: [number, number][] = [];
      const dots: [number, number][] = [];
      for (let j = 0, y = b.y + 1.5; y < b.y + b.h + sp; j++, y += sp / 2) {
        for (let i = 0, x = b.x + 1.5 + (j % 2 ? sp / 2 : 0); x < b.x + b.w + sp; i++, x += sp) {
          if (j % 2) dots.push([x, y]);
          else if (((j / 2 + i) | 0) % 2 === 0) g.poly(starPts(x, y, 5, 1.35, 0.56, -Math.PI / 2 + (rnd() - 0.5) * 0.5));
          else gifts.push([x, y]);
        }
      }
      g.fill({ color: col, alpha: 0.9 });
      for (const [x, y] of gifts) g.rect(x - 0.95, y - 0.8, 1.9, 1.6);
      g.fill({ color: col, alpha: 0.9 });
      for (const [x, y] of gifts) g.moveTo(x, y - 0.8).lineTo(x, y + 0.8).moveTo(x - 0.95, y).lineTo(x + 0.95, y);
      g.stroke({ width: 0.32, color: lighten(col, 0.65), alpha: 0.95 });
      for (const [x, y] of gifts) g.circle(x - 0.32, y - 1.0, 0.3).circle(x + 0.32, y - 1.0, 0.3);
      g.fill({ color: lighten(col, 0.65), alpha: 0.95 });
      for (const [x, y] of dots) g.circle(x, y, 0.38);
      g.fill({ color: col, alpha: 0.7 });
      for (const m of b.rims ?? [{ ...b, r: 0 }]) {
        const rw = Math.min(3, Math.min(m.w, m.h) * 0.22);
        const vx = m.x + m.w * (isRound(m) ? 0.5 : 0.64);
        const hy = m.y + m.h / 2;
        const band = darken(col, 0.12);
        const dark = darken(col, 0.5);
        g.rect(vx - rw / 2, m.y - 1, rw, m.h + 2).rect(m.x - 1, hy - rw / 2, m.w + 2, rw).fill(band);
        g.moveTo(vx - rw / 2, m.y - 1).lineTo(vx - rw / 2, m.y + m.h + 1).moveTo(vx + rw / 2, m.y - 1).lineTo(vx + rw / 2, m.y + m.h + 1);
        g.moveTo(m.x - 1, hy - rw / 2).lineTo(m.x + m.w + 1, hy - rw / 2).moveTo(m.x - 1, hy + rw / 2).lineTo(m.x + m.w + 1, hy + rw / 2);
        g.stroke({ width: 0.4, color: dark, alpha: 0.9 });
        g.moveTo(vx - rw * 0.18, m.y - 1).lineTo(vx - rw * 0.18, m.y + m.h + 1).moveTo(m.x - 1, hy - rw * 0.18).lineTo(m.x + m.w + 1, hy - rw * 0.18);
        g.stroke({ width: 0.35, color: lighten(col, 0.55), alpha: 0.7 });
        // Nœud : deux boucles le long de la pièce, deux pans, un nœud central.
        const L = rw * 1.3;
        for (const side of [-1, 1]) {
          g.poly([vx, hy, vx + side * L * 0.7, hy + L * 0.9, vx + side * L * 0.35, hy + L * 1.05]).fill(band).stroke({ width: 0.4, color: dark });
          g.moveTo(vx, hy)
            .bezierCurveTo(vx + side * L * 1.3, hy - L * 1.05, vx + side * L * 1.35, hy + L * 0.45, vx, hy)
            .fill(lighten(col, 0.08))
            .stroke({ width: 0.4, color: dark });
          g.moveTo(vx + side * L * 0.25, hy - L * 0.1).lineTo(vx + side * L * 0.8, hy - L * 0.3).stroke({ width: 0.3, color: dark, alpha: 0.7 });
        }
        g.circle(vx, hy, rw * 0.32).fill(band).stroke({ width: 0.4, color: dark });
      }
      break;
    }
    default:
      break;
  }
}

// ─── Autocollants ─────────────────────────────────────────────────────

/** Dessine un autocollant centré en (x, y), de taille s (dans le repère de la caisse, nez vers +x). */
export function drawDecal(g: Graphics, id: string, x: number, y: number, s: number, color: number): void {
  const edge = { width: Math.max(0.6, s * 0.06), color: darken(color, 0.7), alpha: 0.9 };
  const P = (pts: [number, number][]) => g.poly(pts.flatMap(([px, py]) => [x + px * s, y + py * s]));
  switch (id) {
    case 'dec:etoile': {
      const pts: [number, number][] = [];
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
        const r = i % 2 ? 0.2 : 0.5;
        pts.push([Math.cos(a) * r, Math.sin(a) * r]);
      }
      P(pts).fill(color).stroke(edge);
      break;
    }
    case 'dec:chevrons':
      for (const dx of [-0.25, 0.1]) P([[dx - 0.15, -0.4], [dx + 0.2, 0], [dx - 0.15, 0.4], [dx - 0.32, 0.4], [dx + 0.03, 0], [dx - 0.32, -0.4]]).fill(color).stroke(edge);
      break;
    case 'dec:cible':
      g.circle(x, y, s * 0.48).stroke({ width: s * 0.1, color });
      g.circle(x, y, s * 0.26).stroke({ width: s * 0.09, color });
      g.circle(x, y, s * 0.08).fill(color);
      break;
    case 'dec:eclair':
      P([[0.05, -0.5], [-0.25, 0.05], [-0.02, 0.05], [-0.12, 0.5], [0.25, -0.08], [0.02, -0.08], [0.15, -0.5]]).fill(color).stroke(edge);
      break;
    case 'dec:flamme':
      g.moveTo(x - 0.45 * s, y)
        .bezierCurveTo(x - 0.2 * s, y - 0.5 * s, x + 0.2 * s, y - 0.25 * s, x + 0.5 * s, y - 0.05 * s)
        .bezierCurveTo(x + 0.25 * s, y + 0.05 * s, x + 0.05 * s, y + 0.45 * s, x - 0.45 * s, y)
        .fill(color)
        .stroke(edge);
      break;
    case 'dec:griffe':
      for (const dy of [-0.28, 0, 0.28]) P([[-0.45, dy - 0.06], [0.4, dy - 0.12], [0.48, dy], [-0.4, dy + 0.06]]).fill(color);
      break;
    case 'dec:ailes':
      for (const side of [-1, 1]) {
        P([[0, 0], [-0.1, side * 0.15], [-0.45, side * 0.5], [-0.25, side * 0.48], [-0.32, side * 0.35], [-0.12, side * 0.36], [-0.18, side * 0.22], [0.05, side * 0.08]]).fill(color).stroke(edge);
      }
      g.circle(x + 0.08 * s, y, 0.09 * s).fill(color);
      break;
    case 'dec:couronne':
      P([[-0.3, -0.42], [0.3, -0.42], [0.3, 0.42], [-0.3, 0.42], [0.12, 0.26], [-0.08, 0.12], [0.18, 0], [-0.08, -0.12], [0.12, -0.26]]).fill(color).stroke(edge);
      break;
    case 'dec:crane':
      // Crâne stylisé (vu de dessus, nez vers +x) : boîte crânienne, orbites, dents.
      g.roundRect(x - 0.42 * s, y - 0.36 * s, 0.62 * s, 0.72 * s, 0.3 * s).fill(color).stroke(edge);
      g.roundRect(x + 0.12 * s, y - 0.2 * s, 0.24 * s, 0.4 * s, 0.06 * s).fill(color).stroke(edge);
      g.circle(x - 0.02 * s, y - 0.15 * s, 0.11 * s).fill(darken(color, 0.8));
      g.circle(x - 0.02 * s, y + 0.15 * s, 0.11 * s).fill(darken(color, 0.8));
      for (const dy of [-0.1, 0, 0.1]) g.rect(x + 0.24 * s, y + dy * s - 0.03 * s, 0.1 * s, 0.05 * s).fill(darken(color, 0.7));
      break;
    case 'dec:dragon': {
      // Dragon enroulé : corps en S, ailes, tête vers +x.
      g.moveTo(x - 0.5 * s, y + 0.3 * s)
        .bezierCurveTo(x - 0.1 * s, y + 0.55 * s, x - 0.2 * s, y - 0.1 * s, x + 0.1 * s, y - 0.05 * s)
        .bezierCurveTo(x + 0.3 * s, y, x + 0.35 * s, y - 0.25 * s, x + 0.5 * s, y - 0.22 * s)
        .stroke({ width: 0.16 * s, color, cap: 'round' });
      P([[0.05, -0.05], [-0.15, -0.5], [0.0, -0.35], [0.1, -0.48], [0.15, -0.2]]).fill(color).stroke(edge);
      P([[0.44, -0.3], [0.58, -0.22], [0.44, -0.14]]).fill(color);
      g.circle(x + 0.45 * s, y - 0.24 * s, 0.03 * s).fill(darken(color, 0.8));
      break;
    }
    default:
      themedDecal(g, id, x, y, s, color, edge);
      break;
  }
}

type Pt = [number, number];

/**
 * Autocollants des collections à thème. Les dessins « debout » (volcan, ancre, blason…) sont écrits en coordonnées
 * d'image (X à droite, Y vers le bas) puis tournés pour que le haut de l'image soit le nez du tank (+x) : ils se lisent
 * droits sur un tank vu nez en haut, comme dans le hangar.
 */
function themedDecal(g: Graphics, id: string, x: number, y: number, s: number, color: number, edge: { width: number; color: number; alpha: number }): void {
  const dark = darken(color, 0.8);
  // Repère du tank (nez vers +x).
  const T = ([px, py]: Pt): Pt => [x + px * s, y + py * s];
  // Repère d'image (haut de l'image = nez du tank).
  const Q = ([X, Y]: Pt): Pt => [x - Y * s, y + X * s];
  const poly = (pts: Pt[], map = Q) => g.poly(pts.flatMap((q) => map(q)));
  const path = (pts: Pt[], map = Q) => {
    const [x0, y0] = map(pts[0]);
    g.moveTo(x0, y0);
    for (const q of pts.slice(1)) g.lineTo(...map(q));
  };
  /** Trait épais à bord sombre : on trace tout en sombre, puis tout en couleur, pour un contour d'un seul tenant. */
  const inked = (draw: () => void, w: number, col = color) => {
    draw();
    g.stroke({ width: w * s + edge.width * 2, color: edge.color, alpha: edge.alpha, cap: 'round', join: 'round' });
    draw();
    g.stroke({ width: w * s, color: col, cap: 'round', join: 'round' });
  };
  const arc = (cx: number, cy: number, r: number, a0: number, a1: number, n = 12, rx = r): Pt[] => Array.from({ length: n + 1 }, (_, k) => [cx + Math.cos(a0 + ((a1 - a0) * k) / n) * rx, cy + Math.sin(a0 + ((a1 - a0) * k) / n) * r] as Pt);
  // Ellipse en coordonnées d'image (rx selon X, ry selon Y).
  const ellipseQ = (X: number, Y: number, rx: number, ry: number) => {
    const [cx, cy] = Q([X, Y]);
    return g.ellipse(cx, cy, ry * s, rx * s);
  };
  switch (id) {
    case 'dec:flocon': {
      inked(() => {
        for (let i = 0; i < 6; i++) {
          const a = (i * Math.PI) / 3;
          const [c, sn] = [Math.cos(a), Math.sin(a)];
          path([[0, 0], [0.48 * c, 0.48 * sn]], T);
          for (const [r, l] of [[0.2, 0.15], [0.34, 0.11]]) {
            for (const side of [-1, 1]) {
              const b = a + side * 1.0;
              path([[r * c, r * sn], [r * c + l * Math.cos(b), r * sn + l * Math.sin(b)]], T);
            }
          }
        }
      }, 0.085);
      g.regularPoly(x, y, 0.11 * s, 6).fill(color).stroke(edge);
      g.regularPoly(x, y, 0.045 * s, 6).fill(darken(color, 0.35));
      break;
    }
    case 'dec:volcan': {
      // Panache de fumée (contour d'un seul tenant), cône, cratère et coulées claires.
      const puffs: [number, number, number][] = [[0.0, -0.17, 0.1], [0.11, -0.28, 0.12], [-0.05, -0.36, 0.11], [0.12, -0.42, 0.09], [-0.12, -0.44, 0.07]];
      for (const [X, Y, r] of puffs) g.circle(...Q([X, Y]), r * s + edge.width).fill({ color: edge.color, alpha: edge.alpha });
      for (const [X, Y, r] of puffs) g.circle(...Q([X, Y]), r * s).fill(darken(color, 0.3));
      poly([[-0.5, 0.46], [-0.14, -0.06], [-0.07, -0.1], [0.07, -0.1], [0.14, -0.06], [0.5, 0.46]]).fill(color).stroke(edge);
      poly([[-0.13, -0.065], [0.13, -0.065], [0.07, -0.02], [-0.07, -0.02]]).fill(dark);
      poly([[-0.06, -0.03], [0.04, -0.03], [0.02, 0.1], [0.12, 0.26], [0.03, 0.22], [-0.04, 0.34], [-0.08, 0.14]]).fill(lighten(color, 0.6));
      poly([[0.06, -0.03], [0.1, -0.03], [0.22, 0.14], [0.15, 0.13]]).fill(lighten(color, 0.6));
      break;
    }
    case 'dec:codebarre': {
      // Étiquette allongée (sens de la chenille), barres d'épaisseurs variées, chiffres stylisés.
      g.roundRect(x - 0.5 * s, y - 0.32 * s, s, 0.64 * s, 0.06 * s).fill(color).stroke(edge);
      const bars = [2, 1, 1, 1, 3, 1, 1, 2, 2, 1, 1, 3, 1, 1, 2, 1];
      const unit = 0.82 / bars.reduce((t, v) => t + v, 0);
      let px = -0.41;
      bars.forEach((w, i) => {
        if (i % 2 === 0) g.rect(x + px * s, y - 0.24 * s, w * unit * s, (i === 0 || i === bars.length - 2 ? 0.48 : 0.38) * s).fill(dark);
        px += w * unit;
      });
      for (let k = 0; k < 6; k++) g.rect(x + (-0.3 + k * 0.11) * s, y + 0.17 * s, 0.07 * s, 0.06 * s).fill(dark);
      break;
    }
    case 'dec:feuille': {
      // Feuille, pointe vers l'avant : limbe, tige, nervure centrale et nervures secondaires.
      inked(() => path([[-0.5, 0.06], [-0.36, 0]], T), 0.06, darken(color, 0.3));
      g.moveTo(x - 0.4 * s, y)
        .bezierCurveTo(x - 0.2 * s, y - 0.44 * s, x + 0.25 * s, y - 0.34 * s, x + 0.5 * s, y)
        .bezierCurveTo(x + 0.25 * s, y + 0.34 * s, x - 0.2 * s, y + 0.44 * s, x - 0.4 * s, y)
        .fill(color)
        .stroke(edge);
      path([[-0.38, 0], [0.4, 0]], T);
      for (const t of [-0.2, 0.0, 0.2]) {
        path([[t, 0], [t + 0.16, -0.2 + Math.abs(t) * 0.25]], T);
        path([[t, 0], [t + 0.16, 0.2 - Math.abs(t) * 0.25]], T);
      }
      g.stroke({ width: Math.max(0.5, 0.045 * s), color: darken(color, 0.45), cap: 'round' });
      break;
    }
    case 'dec:ancre': {
      inked(() => {
        g.circle(...Q([0, -0.38]), 0.085 * s);
        path([[0, -0.29], [0, 0.42]]);
        path([[-0.2, -0.19], [0.2, -0.19]]);
        path(arc(0, 0.06, 0.34, 0.3, Math.PI - 0.3, 14));
      }, 0.075);
      for (const side of [-1, 1]) poly([[side * 0.22, 0.2], [side * 0.43, 0.04], [side * 0.38, 0.27]]).fill(color).stroke(edge);
      poly([[-0.07, 0.38], [0, 0.48], [0.07, 0.38]]).fill(color).stroke(edge);
      break;
    }
    case 'dec:pieuvre': {
      // Pieuvre vue de dessus, tête vers l'avant : huit tentacules ondulés effilés, ventouses, manteau tacheté, yeux.
      const tent = (k: number) => {
        const f = (k - 3.5) / 3.5;
        const y0 = f * 0.15;
        const y1 = f * 0.46;
        const left: Pt[] = [];
        const right: Pt[] = [];
        const mid: Pt[] = [];
        const N = 12;
        for (let i = 0; i <= N; i++) {
          const t = i / N;
          const px = 0.04 - t * 0.54;
          const py = y0 + (y1 - y0) * t + Math.sin(t * Math.PI * 2.4 + k * 1.3) * 0.07 * t;
          mid.push([px, py]);
        }
        for (let i = 0; i <= N; i++) {
          const t = i / N;
          const [ax, ay] = mid[Math.max(0, i - 1)];
          const [bx, by] = mid[Math.min(N, i + 1)];
          const len = Math.hypot(bx - ax, by - ay) || 1;
          const [nx, ny] = [-(by - ay) / len, (bx - ax) / len];
          const w = 0.075 * (1 - t) + 0.012;
          left.push([mid[i][0] + nx * w, mid[i][1] + ny * w]);
          right.unshift([mid[i][0] - nx * w, mid[i][1] - ny * w]);
        }
        return { outline: [...left, ...right], mid };
      };
      const order = [0, 7, 1, 6, 2, 5, 3, 4];
      const tents = order.map(tent);
      for (const t of tents) {
        poly(t.outline, T).fill(color).stroke(edge);
        for (const i of [3, 5, 7]) g.circle(...T(t.mid[i]), Math.max(0.35, (0.03 - i * 0.002) * s)).fill(lighten(color, 0.45));
      }
      g.ellipse(x + 0.02 * s, y, 0.13 * s, 0.2 * s).fill(color);
      g.moveTo(x + 0.5 * s, y)
        .bezierCurveTo(x + 0.5 * s, y - 0.2 * s, x + 0.2 * s, y - 0.27 * s, x + 0.02 * s, y - 0.2 * s)
        .bezierCurveTo(x - 0.06 * s, y - 0.1 * s, x - 0.06 * s, y + 0.1 * s, x + 0.02 * s, y + 0.2 * s)
        .bezierCurveTo(x + 0.2 * s, y + 0.27 * s, x + 0.5 * s, y + 0.2 * s, x + 0.5 * s, y)
        .fill(color)
        .stroke(edge);
      for (const [px, py, r] of [[0.34, -0.07, 0.04], [0.27, 0.08, 0.035], [0.4, 0.06, 0.03], [0.22, -0.12, 0.03]]) g.circle(...T([px, py]), r * s).fill(lighten(color, 0.3));
      for (const side of [-1, 1]) {
        g.circle(...T([0.1, side * 0.15]), 0.07 * s).fill(lighten(color, 0.75)).stroke({ width: edge.width * 0.8, color: edge.color });
        g.circle(...T([0.11, side * 0.15]), 0.04 * s).fill(dark);
      }
      break;
    }
    case 'dec:planete': {
      // Sphère à bandes et ombre, anneau incliné : moitié arrière sous la sphère, moitié avant par-dessus.
      const tilt = -0.35;
      const ring = (t0: number, t1: number): Pt[] =>
        Array.from({ length: 17 }, (_, k) => {
          const t = t0 + ((t1 - t0) * k) / 16;
          const [ex, ey] = [Math.cos(t) * 0.5, Math.sin(t) * 0.15];
          return [ex * Math.cos(tilt) - ey * Math.sin(tilt), ex * Math.sin(tilt) + ey * Math.cos(tilt)] as Pt;
        });
      const ringCol = lighten(color, 0.35);
      inked(() => path(ring(Math.PI, Math.PI * 2)), 0.07, ringCol);
      g.circle(x, y, 0.3 * s).fill(color).stroke(edge);
      for (const Yb of [-0.12, 0.06]) {
        const w = Math.sqrt(0.3 * 0.3 - Yb * Yb) * 0.94;
        path(Array.from({ length: 9 }, (_, k) => {
          const X = -w + (2 * w * k) / 8;
          return [X, Yb + 0.04 * (1 - (X / w) ** 2)] as Pt;
        }));
      }
      g.stroke({ width: 0.06 * s, color: darken(color, 0.25) });
      poly([...arc(0, 0, 0.3, -Math.PI / 3, (Math.PI * 2) / 3, 12), ...arc(0.05, 0.05, 0.27, (Math.PI * 2) / 3, -Math.PI / 3, 12, 0.2)]).fill({ color: darken(color, 0.45), alpha: 0.55 });
      g.circle(...Q([-0.12, -0.13]), 0.06 * s).fill({ color: lighten(color, 0.5), alpha: 0.8 });
      inked(() => path(ring(0, Math.PI)), 0.07, ringCol);
      break;
    }
    case 'dec:fusee': {
      // Fusée, nez vers l'avant : flamme, ailerons, fuselage, coiffe et hublot.
      poly([[-0.27, -0.1], [-0.5, 0], [-0.27, 0.1]], T).fill(lighten(color, 0.6)).stroke(edge);
      for (const side of [-1, 1]) poly([[-0.02, side * 0.14], [-0.3, side * 0.36], [-0.33, side * 0.14]], T).fill(darken(color, 0.3)).stroke(edge);
      poly([[0.5, 0], [0.38, -0.12], [0.2, -0.155], [-0.3, -0.14], [-0.3, 0.14], [0.2, 0.155], [0.38, 0.12]], T).fill(color).stroke(edge);
      poly([[0.5, 0], [0.38, -0.12], [0.3, -0.142], [0.3, 0.142], [0.38, 0.12]], T).fill(darken(color, 0.35)).stroke(edge);
      g.circle(x + 0.1 * s, y, 0.075 * s).fill(dark).stroke({ width: Math.max(0.5, 0.035 * s), color: lighten(color, 0.45) });
      g.rect(x - 0.3 * s, y - 0.03 * s, 0.18 * s, 0.06 * s).fill(darken(color, 0.3));
      break;
    }
    case 'dec:blason': {
      // Écu : bordure intérieure, chevron et besant.
      const shield = (k: number): Pt[] => [[-0.38 * k, -0.44 * k], [0.38 * k, -0.44 * k], [0.38 * k, 0.02 * k], ...Array.from({ length: 6 }, (_, i) => {
        const t = (i + 1) / 6;
        return [0.38 * k * (1 - t) * (1 - t) + 0.36 * k * 2 * t * (1 - t), 0.02 * k * (1 - t) * (1 - t) + 0.34 * k * 2 * t * (1 - t) + 0.5 * k * t * t] as Pt;
      }), ...Array.from({ length: 5 }, (_, i) => {
        const t = 1 - (i + 1) / 6;
        return [-(0.38 * k * (1 - t) * (1 - t) + 0.36 * k * 2 * t * (1 - t)), 0.02 * k * (1 - t) * (1 - t) + 0.34 * k * 2 * t * (1 - t) + 0.5 * k * t * t] as Pt;
      }), [-0.38 * k, 0.02 * k]];
      poly(shield(1)).fill(color).stroke(edge);
      poly(shield(0.78).map(([X, Y]) => [X, Y - 0.01] as Pt)).stroke({ width: Math.max(0.5, 0.04 * s), color: darken(color, 0.4) });
      poly([[-0.26, 0.14], [0, -0.1], [0.26, 0.14], [0.26, 0.27], [0, 0.03], [-0.26, 0.27]]).fill(darken(color, 0.45));
      g.circle(...Q([0, -0.25]), 0.075 * s).fill(darken(color, 0.45));
      break;
    }
    case 'dec:epees': {
      // Deux épées croisées en sautoir : lames, gouttières, gardes, poignées, pommeaux.
      const swords = [-1, 1].map((side) => {
        const hilt: Pt = [side * 0.36, 0.38];
        const tip: Pt = [-side * 0.36, -0.42];
        const len = Math.hypot(tip[0] - hilt[0], tip[1] - hilt[1]);
        const d: Pt = [(tip[0] - hilt[0]) / len, (tip[1] - hilt[1]) / len];
        const n: Pt = [-d[1], d[0]];
        const at = (t: number, o = 0): Pt => [hilt[0] + d[0] * t + n[0] * o, hilt[1] + d[1] * t + n[1] * o];
        return { at, len };
      });
      for (const { at, len } of swords) {
        poly([at(0.22, 0.065), at(len - 0.14, 0.065), at(len), at(len - 0.14, -0.065), at(0.22, -0.065)]).fill(lighten(color, 0.2)).stroke(edge);
        path([at(0.26), at(len - 0.18)]);
        g.stroke({ width: Math.max(0.4, 0.025 * s), color: darken(color, 0.3) });
      }
      for (const { at } of swords) {
        inked(() => path([at(0.06), at(0.2)]), 0.08, darken(color, 0.4));
        poly([at(0.18, -0.13), at(0.23, -0.13), at(0.23, 0.13), at(0.18, 0.13)]).fill(darken(color, 0.15)).stroke(edge);
        g.circle(...Q(at(0.04)), 0.065 * s).fill(darken(color, 0.15)).stroke(edge);
      }
      break;
    }
    case 'dec:danger': {
      // Disque d'avertissement, trèfle à trois pales (une pointe en bas), moyeu central.
      g.circle(x, y, 0.48 * s).fill(color).stroke(edge);
      g.circle(x, y, 0.41 * s).stroke({ width: Math.max(0.5, 0.045 * s), color: dark });
      for (let k = 0; k < 3; k++) {
        const c = Math.PI / 2 + (k * Math.PI * 2) / 3;
        poly([...arc(0, 0, 0.35, c - Math.PI / 6, c + Math.PI / 6, 8), ...arc(0, 0, 0.12, c + Math.PI / 6, c - Math.PI / 6, 4)]).fill(dark);
      }
      g.circle(x, y, 0.075 * s).fill(dark);
      break;
    }
    case 'dec:citrouille': {
      // Citrouille d'Halloween : trois côtes, tige, yeux et nez triangulaires, bouche en dents de scie.
      poly([[-0.04, -0.27], [0.03, -0.46], [0.12, -0.43], [0.06, -0.26]]).fill(darken(color, 0.55)).stroke(edge);
      for (const X of [-0.22, 0.22]) ellipseQ(X, 0.06, 0.22, 0.28).fill(darken(color, 0.15)).stroke(edge);
      ellipseQ(0, 0.05, 0.27, 0.33).fill(color).stroke(edge);
      for (const side of [-1, 1]) path(Array.from({ length: 7 }, (_, k) => [side * (0.07 + 0.06 * Math.sin((k / 6) * Math.PI)), -0.24 + (0.58 * k) / 6] as Pt));
      g.stroke({ width: Math.max(0.4, 0.03 * s), color: darken(color, 0.3) });
      for (const side of [-1, 1]) poly([[side * 0.2, 0.0], [side * 0.06, 0.0], [side * 0.13, -0.13]]).fill(dark);
      poly([[-0.035, 0.08], [0.035, 0.08], [0, 0.03]]).fill(dark);
      poly([[-0.24, 0.12], [-0.15, 0.17], [-0.08, 0.12], [0, 0.18], [0.08, 0.12], [0.15, 0.17], [0.24, 0.12], [0.16, 0.27], [0.07, 0.22], [0, 0.28], [-0.07, 0.22], [-0.16, 0.27]]).fill(dark);
      break;
    }
    case 'dec:sakura': {
      // Fleur de cerisier : cinq pétales échancrés, cœur et étamines.
      for (let k = 0; k < 5; k++) {
        const a = (k * Math.PI * 2) / 5;
        const P = (u: number, v: number): Pt => [x + (u * Math.cos(a) - v * Math.sin(a)) * s, y + (u * Math.sin(a) + v * Math.cos(a)) * s];
        g.moveTo(...P(0.04, 0))
          .bezierCurveTo(...P(0.1, -0.2), ...P(0.36, -0.25), ...P(0.48, -0.11))
          .lineTo(...P(0.38, 0))
          .lineTo(...P(0.48, 0.11))
          .bezierCurveTo(...P(0.36, 0.25), ...P(0.1, 0.2), ...P(0.04, 0))
          .fill(color)
          .stroke(edge);
        g.moveTo(...P(0.1, 0)).lineTo(...P(0.24, 0)).stroke({ width: Math.max(0.5, 0.04 * s), color: darken(color, 0.25), cap: 'round' });
      }
      g.circle(x, y, 0.1 * s).fill(darken(color, 0.3));
      for (let k = 0; k < 5; k++) {
        const a = (k * Math.PI * 2) / 5 + Math.PI / 5;
        g.circle(x + Math.cos(a) * 0.17 * s, y + Math.sin(a) * 0.17 * s, 0.035 * s).fill(darken(color, 0.5));
      }
      break;
    }
    case 'dec:scarabee': {
      // Scarabée vu de dessus, tête vers l'avant : pattes, antennes, élytres fendus et brillants, pronotum, tête.
      const legCol = darken(color, 0.35);
      inked(() => {
        for (const side of [-1, 1]) {
          path([[0.14, side * 0.1], [0.24, side * 0.3], [0.38, side * 0.38]], T);
          path([[0.0, side * 0.14], [-0.04, side * 0.34], [0.04, side * 0.46]], T);
          path([[-0.14, side * 0.14], [-0.28, side * 0.32], [-0.26, side * 0.46]], T);
          path([[0.38, side * 0.04], [0.46, side * 0.13], [0.5, side * 0.09]], T);
        }
      }, 0.045, legCol);
      g.ellipse(x - 0.08 * s, y, 0.3 * s, 0.22 * s).fill(color).stroke(edge);
      g.ellipse(x + 0.22 * s, y, 0.11 * s, 0.17 * s).fill(darken(color, 0.12)).stroke(edge);
      g.ellipse(x + 0.36 * s, y, 0.07 * s, 0.1 * s).fill(darken(color, 0.3)).stroke(edge);
      g.moveTo(x + 0.12 * s, y).lineTo(x - 0.37 * s, y).stroke({ width: Math.max(0.5, 0.035 * s), color: darken(color, 0.55) });
      for (const side of [-1, 1]) g.ellipse(x - 0.06 * s, y + side * 0.1 * s, 0.13 * s, 0.035 * s).fill({ color: lighten(color, 0.5), alpha: 0.8 });
      break;
    }
    case 'dec:aureole': {
      // Auréole : rayons alternés, halo diffus, anneau lumineux à reflet clair.
      for (let k = 0; k < 12; k++) {
        const a = (k * Math.PI) / 6 + Math.PI / 12;
        const r = k % 2 ? 0.5 : 0.43;
        poly([[Math.cos(a - 0.17) * 0.3, Math.sin(a - 0.17) * 0.3], [Math.cos(a) * r, Math.sin(a) * r], [Math.cos(a + 0.17) * 0.3, Math.sin(a + 0.17) * 0.3]], T).fill(lighten(color, 0.15)).stroke({ ...edge, width: edge.width * 0.35 });
      }
      g.circle(x, y, 0.28 * s).stroke({ width: 0.24 * s, color, alpha: 0.35 });
      g.circle(x, y, 0.28 * s).stroke({ width: 0.12 * s + edge.width * 2, color: edge.color, alpha: edge.alpha });
      g.circle(x, y, 0.28 * s).stroke({ width: 0.12 * s, color });
      g.circle(x, y, 0.28 * s).stroke({ width: Math.max(0.4, 0.035 * s), color: lighten(color, 0.65) });
      break;
    }
    case 'dec:sapin': {
      // Sapin : tronc, trois étages d'un seul tenant (flanc droit ombré), boules claires, étoile au sommet.
      poly([[-0.07, 0.3], [0.07, 0.3], [0.07, 0.46], [-0.07, 0.46]]).fill(darken(color, 0.55)).stroke(edge);
      const tree: Pt[] = [[0, -0.32], [0.17, -0.08], [0.09, -0.08], [0.29, 0.13], [0.18, 0.13], [0.41, 0.35], [-0.41, 0.35], [-0.18, 0.13], [-0.29, 0.13], [-0.09, -0.08], [-0.17, -0.08]];
      poly(tree).fill(color);
      poly([[0, -0.32], [0.17, -0.08], [0.09, -0.08], [0.29, 0.13], [0.18, 0.13], [0.41, 0.35], [0, 0.35]]).fill({ color: darken(color, 0.32), alpha: 0.6 });
      poly(tree).stroke(edge);
      for (const [X, Y] of [[-0.14, 0.26], [0.17, 0.24], [0.05, 0.05], [-0.09, -0.02]]) g.circle(...Q([X, Y]), 0.045 * s);
      g.fill(lighten(color, 0.7));
      const [sx, sy] = Q([0, -0.36]);
      // Pointe vers le haut de l'image, c'est-à-dire vers le nez (+x) dans le repère du tank.
      g.poly(starPts(sx, sy, 5, 0.15 * s, 0.065 * s, 0)).fill(lighten(color, 0.55)).stroke(edge);
      break;
    }
    case 'dec:renne': {
      // Renne de face : bois ramifiés, oreilles, tête effilée vers le museau clair, nez et yeux sombres.
      inked(() => {
        for (const sd of [-1, 1]) {
          path([[sd * 0.08, -0.14], [sd * 0.18, -0.27], [sd * 0.2, -0.47]]);
          path([[sd * 0.18, -0.27], [sd * 0.38, -0.32], [sd * 0.45, -0.44]]);
          path([[sd * 0.19, -0.37], [sd * 0.09, -0.46]]);
          path([[sd * 0.31, -0.3], [sd * 0.33, -0.43]]);
        }
      }, 0.06, darken(color, 0.3));
      for (const sd of [-1, 1]) poly([[sd * 0.12, -0.1], [sd * 0.36, -0.13], [sd * 0.4, -0.05], [sd * 0.14, -0.01]]).fill(darken(color, 0.15)).stroke(edge);
      poly([[-0.15, -0.17], [0.15, -0.17], [0.2, -0.04], [0.16, 0.14], [0.13, 0.3], [0.08, 0.4], [0, 0.44], [-0.08, 0.4], [-0.13, 0.3], [-0.16, 0.14], [-0.2, -0.04]]).fill(color).stroke(edge);
      ellipseQ(0, 0.32, 0.11, 0.1).fill(lighten(color, 0.4));
      g.circle(...Q([0, 0.39]), 0.07 * s).fill(dark).stroke({ width: edge.width * 0.6, color: edge.color });
      g.circle(...Q([-0.02, 0.37]), 0.022 * s).fill(lighten(color, 0.6));
      for (const sd of [-1, 1]) g.circle(...Q([sd * 0.08, 0.04]), 0.04 * s).fill(dark);
      break;
    }
    case 'dec:etoilenoel': {
      // Étoile de Noël : halo, huit branches (les verticales très longues, la plus longue vers le bas), facettes en relief.
      g.circle(x, y, 0.27 * s).fill({ color: lighten(color, 0.35), alpha: 0.3 });
      const rays = [0.43, 0.17, 0.3, 0.17, 0.5, 0.17, 0.3, 0.17];
      const tip = (k: number): Pt => [Math.cos(-Math.PI / 2 + (k * Math.PI) / 4) * rays[k], Math.sin(-Math.PI / 2 + (k * Math.PI) / 4) * rays[k]];
      const notch = (k: number): Pt => [Math.cos(-Math.PI / 2 + ((k + 0.5) * Math.PI) / 4) * 0.065, Math.sin(-Math.PI / 2 + ((k + 0.5) * Math.PI) / 4) * 0.065];
      poly(Array.from({ length: 8 }, (_, k) => [tip(k), notch(k)]).flat()).fill(color).stroke(edge);
      for (let k = 0; k < 8; k++) poly([[0, 0], tip(k), notch(k)]).fill({ color: lighten(color, 0.4), alpha: 0.75 });
      g.circle(x, y, 0.055 * s).fill(lighten(color, 0.8));
      break;
    }
    case 'dec:houx': {
      // Houx : deux feuilles épineuses en V (nervure centrale), trois baies sombres à reflet.
      const leaf = (bx: number, by: number, ang: number, L: number, W: number) => {
        const d: Pt = [Math.cos(ang), Math.sin(ang)];
        const at = (u: number, v: number): Pt => [bx + d[0] * u * L - d[1] * v, by + d[1] * u * L + d[0] * v];
        const width = (t: number) => W * Math.pow(Math.sin(Math.PI * Math.min(1, t)), 0.7);
        const spikes = [0.16, 0.38, 0.6, 0.8];
        const side = (sg: number): Pt[] =>
          spikes.flatMap((t, i) => {
            const tm = i + 1 < spikes.length ? (t + spikes[i + 1]) / 2 : (t + 1) / 2;
            return [at(t, sg * width(t) * 1.15), at(tm, sg * width(tm) * 0.62)];
          });
        return { outline: [at(0, 0), ...side(1), at(1.06, 0), ...side(-1).reverse()], rib: [at(0.02, 0), at(0.92, 0)] };
      };
      const leaves = [leaf(-0.02, 0.07, Math.PI - 0.38, 0.45, 0.16), leaf(0.02, 0.07, 0.38, 0.45, 0.16)];
      leaves.forEach((l, i) => {
        poly(l.outline).fill(i ? darken(color, 0.14) : color).stroke(edge);
        path(l.rib);
        g.stroke({ width: Math.max(0.5, 0.04 * s), color: darken(color, 0.5), cap: 'round' });
      });
      for (const [X, Y] of [[-0.09, -0.03], [0.09, -0.03], [0, -0.17]] as Pt[]) {
        g.circle(...Q([X, Y]), 0.1 * s).fill(darken(color, 0.45)).stroke(edge);
        g.circle(...Q([X - 0.03, Y - 0.03]), 0.032 * s).fill(lighten(color, 0.6));
      }
      break;
    }
    default:
      break;
  }
}

// ─── Assemblage ───────────────────────────────────────────────────────

/** Couche d'une pièce : base + (matière, motif, autocollant) découpés par la silhouette. */
function paintPart(parent: Container, p: PartPaint, shape: (g: Graphics) => Box, seed: number, decal?: { x: number; y: number; s: number }[]): Box {
  const base = new Graphics();
  const box = shape(base);
  base.fill(baseFill(p)).stroke({ width: 2.5, color: outlineOf(p.color) });
  parent.addChild(base);
  const detail = new Graphics();
  materialOverlay(detail, p, box, seed);
  patternOverlay(detail, p, box, seed);
  if (p.dec && decal) for (const d of decal) drawDecal(detail, p.dec, d.x, d.y, d.s, p.decColor);
  if (p.mat === 'mat:neon') {
    // Néon : liseré lumineux à l'intérieur de la silhouette.
    shape(detail);
    detail.stroke({ width: 5, color: lighten(p.color, 0.2), alpha: 0.35 });
    shape(detail);
    detail.stroke({ width: 1.6, color: lighten(p.color, 0.65), alpha: 1 });
  } else if (p.mat === 'mat:spectral') {
    // Spectral : liseré intérieur diffus et pâle (plusieurs traits larges et transparents).
    const pale = lighten(vivid(p.color, 0.45, 0.55, 0.7), 0.5);
    for (const [w, al] of [[10, 0.08], [7, 0.12], [4.6, 0.22], [3.4, 0.4]]) {
      shape(detail);
      detail.stroke({ width: w, color: pale, alpha: al });
    }
  } else if (p.mat === 'mat:laque') {
    // Laque : fin liseré doré juste à l'intérieur du contour (le contour sombre en couvre la moitié extérieure).
    shape(detail);
    detail.stroke({ width: 6.2, color: darken(lacquerOf(p.color), 0.55), alpha: 0.9 });
    shape(detail);
    detail.stroke({ width: 4.6, color: 0xd9b45a, alpha: 1 });
  } else if (p.mat === 'mat:paindepices' && box.rims) {
    icing(detail, box.rims, gingerOf(p.color));
  } else if (p.mat === 'mat:velours' && box.rims) {
    furTrim(detail, box.rims, velvetOf(p.color), seed);
  }
  const mask = new Graphics();
  shape(mask);
  mask.fill(0xffffff);
  detail.mask = mask;
  parent.addChild(detail, mask);
  // Contour par-dessus le détail : la silhouette reste nette quel que soit le motif.
  const edge = new Graphics();
  shape(edge);
  edge.stroke({ width: 2.5, color: outlineOf(p.color) });
  parent.addChild(edge);
  return box;
}

/** Échantillon d'une peinture (matière, motif, autocollant) sur une plaque arrondie : vignettes de l'atelier. */
export function drawSwatch(p: PartPaint, w: number, h: number): Container {
  const c = new Container();
  paintPart(c, p, (g) => {
    g.roundRect(-w / 2, -h / 2, w, h, Math.min(w, h) * 0.16);
    return { x: -w / 2, y: -h / 2, w, h, rims: [{ x: -w / 2, y: -h / 2, w, h, r: Math.min(w, h) * 0.16 }] };
  }, 7);
  if (p.dec) {
    // Autocollant tourné comme sur un tank nez en haut (hangar) : le haut du dessin est le nez (+x du repère).
    const d = new Graphics();
    drawDecal(d, p.dec, 0, 0, Math.min(w, h) * 0.7, p.decColor);
    d.rotation = -Math.PI / 2;
    c.addChild(d);
  }
  return c;
}

/** Palette « classique » dérivée d'une pièce, pour les détails de famille et les tubes. */
function paletteOf(p: PartPaint, outlineFrom = p.color): Palette {
  return { hull: p.color, plate: lighten(p.color, 0.18), tread: darken(p.color, 0.3), barrel: p.color, band: darken(p.color, 0.32), outline: outlineOf(outlineFrom) };
}

export interface PaintedTank {
  hull: Container;
  turret: Container;
  auto: Graphics;
  hasAuto: boolean;
}

/**
 * Tank complet peint selon `look` : caisse (chenilles + caisse + détails de famille et galons), tourelle (tubes + dôme,
 * cœur à la couleur du camp), tourelles automatiques. Même repère et même échelle que drawTankParts.
 */
export function drawPaintedTank(cls: ClassNode, look: TankLook, accent: number, seedBase = 1, decor = 0): PaintedTank {
  const L = look.parts;
  const hull = new Container();
  if (decor === 2) {
    // Châssis lourd : plaques latérales sous les chenilles, comme le dessin classique (hitbox inchangée).
    const tp = paletteOf(L.treads);
    const plates = new Graphics();
    for (const side of [-1, 1]) plates.roundRect(-0.6 * R, side * 0.86 * R - 0.08 * R, 1.2 * R, 0.16 * R, 0.05 * R).fill(tp.band).stroke({ width: 2, color: tp.outline });
    hull.addChild(plates);
  }
  paintPart(hull, L.treads, treadsShape, seedBase * 11 + 1, [
    { x: 0, y: -0.66 * R, s: 0.36 * R },
    { x: 0, y: 0.66 * R, s: 0.36 * R },
  ]);
  paintPart(hull, L.hull, hullShape, seedBase * 11 + 2);
  shadeHull(hull, seedBase);
  const hp = paletteOf(L.hull);
  const det = new Graphics();
  // Nez (sens de la caisse) et rivets, puis détails propres à la famille et galons de rang.
  det.poly([0.64 * R, -0.18 * R, 0.76 * R, 0, 0.64 * R, 0.18 * R]).fill(hp.plate).stroke({ width: 1.5, color: hp.outline });
  if (decor !== 1) for (const [x, y] of [[-0.52, -0.4], [0.52, -0.4], [-0.52, 0.4], [0.52, 0.4]]) det.circle(x * R, y * R, 0.05 * R).fill(hp.outline);
  drawHullFamily(det, cls, hp);
  drawFamilyDetail(det, cls, hp);
  drawRankPips(det, cls, hp);
  hull.addChild(det);

  const turret = new Container();
  const bp = paletteOf(L.barrels);
  const barrels = cls.weapon.barrels.filter((b) => !b.auto);
  const barrelShape = (g: Graphics): Box => {
    // Silhouette des tubes : on retrace chaque tube en blanc (même géométrie que drawBarrel).
    const white: Palette = { hull: 0xffffff, plate: 0xffffff, tread: 0xffffff, barrel: 0xffffff, band: 0xffffff, outline: 0xffffff };
    for (const b of barrels) drawBarrel(g, b, white, 0xffffff);
    return { x: -1.6 * R, y: -1.6 * R, w: 3.2 * R, h: 3.2 * R };
  };
  if (barrels.length) {
    // Les tubes gardent leur dessin (embouchures, trappes à la couleur du camp) ; matière et motif s'y ajoutent.
    const base = new Graphics();
    for (const b of barrels) drawBarrel(base, b, bp, accent);
    turret.addChild(base);
    const detail = new Graphics();
    const box = { x: -0.2 * R, y: -1.6 * R, w: 2.4 * R, h: 3.2 * R };
    if (UNDERLAID.has(L.barrels.mat)) detail.rect(box.x, box.y, box.w, box.h).fill(baseFill(L.barrels));
    materialOverlay(detail, L.barrels, box, seedBase * 11 + 3);
    patternOverlay(detail, L.barrels, box, seedBase * 11 + 3, true);
    const mask = new Graphics();
    barrelShape(mask);
    detail.mask = mask;
    // Contour des tubes, puis embouchures et pièces à la couleur du camp par-dessus le motif.
    // Remplissage transparent (style Pixi accepté par fill) : seuls les traits de drawBarrel restent.
    const clear = { color: 0, alpha: 0 } as unknown as number;
    const edge = new Graphics();
    for (const b of barrels) drawBarrel(edge, b, { hull: clear, plate: clear, tread: clear, barrel: clear, band: clear, outline: bp.outline }, clear);
    const tips = new Graphics();
    for (const b of barrels) drawBarrel(tips, b, bp, accent, true);
    // Ombrage des tubes sous les contours et sous les pièces à la couleur du camp (jamais assombries).
    turret.addChild(detail, mask);
    shadeBarrels(turret, cls);
    turret.addChild(edge, tips);
  }
  paintPart(turret, L.turret, domeShape, seedBase * 11 + 4);
  shadeDome(turret);
  glossCore(turret, accent, outlineOf(L.turret.color));

  const auto = new Graphics();
  const autos = cls.weapon.barrels.filter((b) => b.auto);
  if (autos.length) {
    for (const b of autos) drawBarrel(auto, b, bp, accent);
    for (const b of autos) {
      const a = (b.angle * Math.PI) / 180;
      const ox = -Math.sin(a) * b.offset * R;
      const oy = Math.cos(a) * b.offset * R;
      auto.circle(ox, oy, 0.24 * R).fill(paletteOf(L.turret).plate).stroke({ width: 1.5, color: outlineOf(L.turret.color) });
      auto.circle(ox, oy, 0.1 * R).fill(accent);
    }
  }
  return { hull, turret, auto, hasAuto: autos.length > 0 };
}

/** Pièce → libellé court (éditeur, planche). */
export const PART_LABEL: Record<PartId, string> = { hull: 'Caisse', treads: 'Chenilles', turret: 'Tourelle', barrels: 'Canons' };
